/**
 * Translation dictionaries. Arabic is the primary locale (the product is
 * Arabic-first, RTL); English is the secondary. Both dictionaries MUST expose
 * the same keys — `TranslationKey` is derived from `en` and the `ar` entry is
 * type-checked against it below.
 */
export const en = {
  "app.name": "Cadeau CRM",
  "app.tagline": "Secure, multi-tenant CRM for social commerce",
  "nav.primary": "Primary navigation",
  "nav.bottom": "Bottom navigation",
  "nav.more": "More",
  "nav.back": "Back",
  // Subtitles under each destination in the mobile navigation drawer. Keyed by
  // route rather than by label so a renamed label never orphans a subtitle.
  "nav.desc.dashboard": "Overview of today's activity",
  "nav.desc.orders": "Track and fulfil customer orders",
  "nav.desc.customers": "Contacts, history, and segments",
  "nav.desc.products": "Catalogue, pricing, and variants",
  "nav.desc.inventory": "Warehouses, stock, and transfers",
  "nav.desc.finance": "Expenses, purchase orders, and reports",
  "nav.desc.analytics": "Profitability and product performance",
  "nav.desc.masterData": "Shared lists that power every screen",
  "nav.desc.team": "Members, roles, and permissions",
  "nav.desc.settings": "Company, billing, and preferences",
  "nav.desc.admin": "Platform-wide tenants and features",
  "nav.menu.darkMode": "Dark mode",
  "nav.dashboard": "Dashboard",
  "nav.orders": "Orders",
  "nav.customers": "Customers",
  "nav.products": "Products",
  "nav.inventory": "Inventory",
  "nav.settings": "Settings",
  "sidebar.collapse": "Collapse sidebar",
  "sidebar.expand": "Expand sidebar",
  "sidebar.section.core": "Core settings",
  "user.account": "Account",
  "user.badge.premium": "Premium user",
  "user.profile": "Profile",
  "user.settings": "Settings",
  "user.signOut": "Sign out",
  "placeholder.description": "This section is part of a later epic and is not built yet.",
  "command.trigger": "Search…",
  "command.placeholder": "Type a command or search…",
  "command.empty": "No results found.",
  "command.navigation": "Navigation",
  "command.actions": "Actions",
  "command.switchLanguage": "Switch language",
  "combobox.placeholder": "Select…",
  "combobox.search": "Search…",
  "combobox.empty": "No results found.",
  "combobox.loading": "Searching…",
  "datePicker.placeholder": "Pick a date",
  "datePicker.clear": "Clear",
  "form.optional": "optional",
  "filters.title": "Filters",
  "actions.toggleTheme": "Toggle theme",
  "actions.toggleLanguage": "العربية",
  "install.action": "Install app",
  "install.help.title": "Install the app on your device",
  "install.help.close": "Done",
  "install.help.ios.intro": "In Safari, add Cadeau CRM to your home screen:",
  "install.help.ios.step1": "Tap the Share button in the browser bar.",
  "install.help.ios.step2": "Choose “Add to Home Screen”.",
  "install.help.ios.step3": "Tap “Add” — the app then sits alongside your other apps.",
  "states.loading": "Loading…",
  "states.empty.title": "Nothing here yet",
  "states.empty.description": "When there is data to show, it will appear here.",
  "states.error.title": "Something went wrong",
  "states.error.description": "We could not complete the request. Please try again.",
  "states.error.retry": "Try again",
  "states.offline": "You are offline — data will not load until the connection returns.",
  "dashboard.title": "Dashboard",
  "dashboard.subtitle": "Today's business at a glance.",
  "dashboard.kpi.title": "Key figures",
  "dashboard.kpi.period": "Period",
  "dashboard.kpi.period.today": "Today",
  "dashboard.kpi.period.month": "This month",
  "dashboard.kpi.period.lastMonth": "Last month",
  "dashboard.kpi.period.quarter": "3 months",
  "dashboard.kpi.period.year": "Year",
  "dashboard.kpi.period.all": "All time",
  "dashboard.kpi.vs.today": "vs yesterday",
  "dashboard.kpi.vs.month": "vs last month",
  "dashboard.kpi.vs.lastMonth": "vs prior month",
  "dashboard.kpi.vs.quarter": "vs prior 3m",
  "dashboard.kpi.vs.year": "vs prior year",
  "dashboard.kpi.vs.all": "all time",
  "dashboard.kpi.expectedRevenue": "Expected revenue",
  "dashboard.kpi.averageOrder": "Average order",
  "dashboard.kpi.shipped": "Shipped",
  "dashboard.kpi.processing": "In progress",
  "dashboard.kpi.ready": "Ready",
  "dashboard.loadFailed": "Could not load this widget.",
  "dashboard.kpi.orders": "Orders",
  "dashboard.quickActions.newOrder": "New order",
  "dashboard.quickActions.newCustomer": "New customer",
  "dashboard.quickActions.newProduct": "New product",
  "dashboard.sales.title": "Sales",
  "dashboard.statusChart.title": "Orders by status",
  "dashboard.recentOrders.title": "Recent orders",
  "dashboard.activity.title": "Recent activity",
  "dashboard.activity.line": "Order #{{order}} → {{status}}",
  "dashboard.lowStock.title": "Low stock alerts",
  "dashboard.lowStock.empty": "No low-stock items right now.",
  "dashboard.notifications.title": "Notifications",
  "dashboard.notifications.unread": "{{count}} unread",
  "notFound.title": "Page not found",
  "notFound.description": "The page you are looking for does not exist.",
  "notFound.back": "Back to home",
  "auth.login.title": "Sign in",
  "auth.login.subtitle": "Sign in to your Cadeau CRM account.",
  "auth.login.submit": "Sign in",
  "auth.login.noAccount": "Don't have an account?",
  "auth.login.toRegister": "Create one",
  "auth.register.title": "Create your account",
  "auth.register.subtitle": "Start using Cadeau CRM.",
  "auth.register.submit": "Create account",
  "auth.register.haveAccount": "Already have an account?",
  "auth.register.toLogin": "Sign in",
  "auth.field.email": "Email",
  "auth.field.password": "Password",
  "auth.field.fullName": "Full name",
  "auth.field.phone": "Phone",
  "auth.field.optional": "optional",
  "auth.field.totp": "Authentication code",
  "auth.twoFactor.title": "Two-step verification",
  "auth.twoFactor.subtitle": "Enter the 6-digit code from your authenticator app.",
  "auth.twoFactor.submit": "Verify",
  "auth.twoFactor.back": "Back",
  "auth.submitting": "Please wait…",
  "auth.error.invalidCredentials": "The email or password is incorrect.",
  "auth.error.invalidCode": "That code is incorrect or has expired.",
  "auth.error.emailTaken": "An account with this email already exists.",
  "auth.error.validation": "Please check the highlighted fields.",
  "auth.invalid.email": "Enter a valid email address.",
  "auth.invalid.password": "The password must be at least 8 characters.",
  "auth.invalid.fullName": "The name is too long (200 characters at most).",
  "auth.invalid.phone": "Use English digits only, e.g. 01001234567.",
  "auth.error.rateLimited": "Too many attempts. Please try again later.",
  "auth.error.generic": "Something went wrong. Please try again.",
  "company.switcher.label": "Active company",
  "company.switcher.none": "No company yet",
  "company.switcher.switch": "Switch company",
  "nav.admin": "Platform Admin",
  "nav.team": "Team",
  "team.title": "Team",
  "team.subtitle": "Roles, permissions and the people on your team.",
  "team.hero.badge": "Flexible, secure access",
  "team.hero.badgeHint": "Each member sees only what their role allows.",
  "team.roles.title": "Roles & permissions",
  "team.roles.search": "Search roles…",
  "team.sections.label": "Team sections",
  "team.sections.rolesDesc": "What each role is allowed to do",
  "team.sections.membersDesc": "Members and pending invitations",
  "team.sections.rolesCount": "{{count}} roles",
  "team.sections.membersCount": "{{count}} members",
  "team.sections.pendingCount": "{{count}} pending",
  "team.members.search": "Search members…",
  "team.members.noMatch": "No members match your search.",
  "team.vendors.title": "Vendor accounts",
  "team.vendors.empty": "No vendor accounts yet.",
  "team.roles.add": "Add new role",
  "team.roles.edit": "Edit role",
  "team.roles.viewAll": "View all permissions",
  "team.roles.topPermissions": "Key permissions",
  "team.roles.more": "+{{count}} more",
  "team.roles.permissionCount": "{{count}} permissions",
  "team.roles.memberCount": "{{count}} users",
  "team.roles.actionsFor": "Actions for {{role}}",
  "team.roles.empty": "No roles are defined yet.",
  "team.roles.noMatch": "No roles match your search.",
  "team.roles.allPermissionsTitle": "{{role}} permissions",
  "team.roles.noPermissions": "This role has no permissions.",
  "team.roles.close": "Close",
  "team.roleDialog.addTitle": "Add new role",
  "team.roleDialog.editTitle": "Edit role: {{role}}",
  "team.roleDialog.name": "Role name",
  "team.roleDialog.description": "Description",
  "team.roleDialog.permissions": "Permissions",
  "team.roleDialog.save": "Save role",
  "team.roleDialog.cancel": "Cancel",
  "team.roleDialog.comingSoon": "Saving roles will be available in the next phase.",
  "team.roleDialog.loadError": "Could not load the permission list.",
  "team.members.section": "Team",
  "team.members.permissionsUnavailable": "Permissions could not be loaded.",
  "team.members.noPermissions": "No permissions assigned.",
  "team.members.joined": "Joined {{date}}",
  "team.members.actionsFor": "Actions for {{name}}",
  "team.invitations.expires": "Expires {{date}}",
  "team.error.noCompany": "Select a company first.",
  "team.error.unauthorized": "Your session has expired. Please sign in again.",
  "team.error.generic": "Something went wrong. Please try again.",
  "team.status.active": "Active",
  "team.status.pending": "Pending",
  "team.status.accepted": "Accepted",
  "team.status.revoked": "Revoked",
  "team.status.expired": "Expired",
  "team.role.custom": "Custom",
  "team.members.title": "Team members",
  "team.members.empty": "No team members yet.",
  "team.members.column.name": "Name",
  "team.members.column.email": "Email",
  "team.members.column.role": "Role",
  "team.members.column.status": "Status",
  "team.members.column.joinedAt": "Joined",
  "team.members.actions.remove": "Remove",
  "team.members.remove.confirmTitle": "Remove team member?",
  "team.members.remove.confirmDescription":
    "{{name}} will lose access to this company immediately. This cannot be undone.",
  "team.members.remove.confirm": "Remove member",
  "team.members.remove.cancel": "Cancel",
  "team.members.remove.success": "Member removed.",
  "team.invitations.title": "Pending invitations",
  "team.invitations.empty": "No invitations yet.",
  "team.invitations.column.role": "Role",
  "team.invitations.column.status": "Status",
  "team.invitations.column.expiresAt": "Expires",
  "team.invitations.actions.revoke": "Revoke",
  "team.invitations.revoke.confirmTitle": "Revoke invitation?",
  "team.invitations.revoke.confirmDescription":
    "The {{role}} invitation code will stop working immediately. Anyone still holding it will not be able to join.",
  "team.invitations.revoke.confirm": "Revoke invitation",
  "team.invitations.revoke.cancel": "Cancel",
  "team.invitations.revoke.success": "Invitation revoked.",
  "team.invite.button": "Invite member",
  "team.invite.title": "Invite a team member",
  "team.invite.field.role": "Role type",
  "team.invite.field.predefinedRole": "Role",
  "team.invite.roleType.predefined": "Predefined role",
  "team.invite.roleType.custom": "Custom role",
  "team.invite.role.owner": "Owner",
  "team.invite.role.manager": "Manager",
  "team.invite.role.store_manager": "Store Manager",
  "team.invite.role.call_center": "Call Center",
  "team.invite.role.warehouse": "Warehouse",
  "team.invite.role.finance": "Finance",
  "team.invite.role.marketing": "Marketing",
  "team.role.vendor": "Vendor",
  "team.role.owner.desc": "Full access to everything in the company.",
  "team.role.manager.desc":
    "Full operational access across the company, like Owner, but cannot manage team roles and permissions unless granted separately.",
  "team.role.store_manager.desc": "Runs day-to-day operations across the store.",
  "team.role.call_center.desc": "Handles orders and customer contact.",
  "team.role.warehouse.desc": "Manages stock, fulfilment, and shipping.",
  "team.role.finance.desc": "Owns finance, invoicing, and reconciliation.",
  "team.role.marketing.desc": "Analytics and customer engagement.",
  "team.role.vendor.desc":
    "Manages their own warehouse only — scoped automatically to the warehouse they joined.",
  "team.permission.orders.vendorGroupsOverride.name": "Override vendor status",
  "team.permission.orders.vendorGroupsOverride.desc":
    "Set any vendor group's status, including moving it backward.",
  "team.permission.messaging.send.name": "Reply in conversations",
  "team.permission.messaging.send.desc":
    "Post messages in a conversation you are a participant in.",
  "team.invite.manager.grantAccessManage":
    "Also let this Manager manage team members and permissions",
  "team.invite.custom.description":
    "This permission set belongs only to this invitation — once accepted, it becomes that one member's permissions. It is not a reusable role; inviting someone else again means choosing permissions from scratch.",
  "team.invite.custom.loadError": "Couldn't load the available permissions. Please try again.",
  "team.invite.custom.empty": "No permissions are available for your company's current plan.",
  "team.invite.custom.selected": "{{count}} permission(s) selected",
  "team.invite.custom.selectAll": "Select all",
  "team.invite.custom.clearAll": "Clear all",
  "team.invite.back": "Back",
  "team.invite.next": "Next",
  "team.invite.cancel": "Cancel",
  "team.invite.submit": "Send invitation",
  "team.invite.submitting": "Sending…",
  "team.module.core": "General",
  "team.module.masterData": "Data",
  "team.module.products": "Products",
  "team.module.inventory": "Inventory",
  "team.module.customers": "Customers",
  "team.module.orders": "Orders",
  "team.module.shipping": "Shipping",
  "team.module.finance": "Finance",
  "team.module.analytics": "Analytics",
  "team.module.notifications": "Notifications",
  "team.module.messaging": "Conversations",
  "team.module.storefrontIntegration": "Integrations",
  "team.permission.action.read": "View",
  "team.permission.action.manage": "Manage",
  "team.permission.desc.read": "View {{module}} without changing anything.",
  "team.permission.desc.manage": "Create, edit and delete in {{module}}.",
  "team.permission.access.read.name": "View permissions",
  "team.permission.access.read.desc": "See the team, its roles and permission templates.",
  "team.permission.access.manage.name": "Manage permissions",
  "team.permission.access.manage.desc":
    "Invite members, remove them, and change their permissions.",
  "team.permission.orders.assign.name": "Assign orders",
  "team.permission.orders.assign.desc":
    "Assign orders to team members, and see every order in the company rather than only the assigned ones.",
  "team.permission.integrations.manage.name": "Manage store connections",
  "team.permission.integrations.manage.desc":
    "Create, rotate and revoke the keys connecting an online storefront.",
  "team.invite.custom.unavailable": "Not in your plan",
  "team.invite.custom.unavailableHint":
    "Greyed-out permissions need a feature your current plan does not include.",
  "team.invite.custom.summary": "{{permissions}} permission(s) across {{modules}} module(s)",
  "team.invite.custom.moduleCount": "{{selected}}/{{total}}",
  "team.invite.custom.moduleSelectAll": "Select all in this section",
  "team.result.title": "Invitation created",
  "team.result.description":
    "This code is shown only once. Copy it now and send it to the person you are inviting — they sign up with their own email and enter this code to join. You won't be able to see it again.",
  "team.result.code.label": "Invitation code",
  "team.result.copy": "Copy",
  "team.result.copied": "Copied!",
  "team.result.copyFailed":
    "Couldn't copy automatically — please select and copy the code manually.",
  "team.result.done": "Done",
  "admin.title": "Platform Administration",
  "admin.subtitle": "Manage companies, their plans, and feature access across the platform.",
  "admin.companies": "Companies",
  "admin.plan": "Plan",
  "admin.noPlan": "No plan",
  "admin.setPlan": "Set plan",
  "admin.features": "Features",
  "admin.loadMore": "Load more",
  "admin.empty": "No companies yet.",
  "admin.enable": "Enable",
  "admin.disable": "Disable",
  "admin.saved": "Saved",
  "admin.saveFailed": "Could not save. Please try again.",
  "admin.forbidden": "You need platform administrator access to view this page.",
  "nav.masterData": "Master Data",
  "md.title": "Master Data",
  "md.subtitle": "Shared reference data every module builds on.",
  "md.resources": "Resources",
  "md.readOnly": "This reference data is system-managed and read-only.",
  "md.forbidden": "You do not have access to master data.",
  "md.empty": "Nothing here yet.",
  "md.loadMore": "Load more",
  "md.saved": "Saved",
  "md.saveFailed": "Could not save. Please try again.",
  "md.status.title": "Status",
  "md.status.active": "Active",
  "md.status.inactive": "Inactive",
  "md.actions.create": "New",
  "md.actions.edit": "Edit",
  "md.actions.save": "Save",
  "md.actions.cancel": "Cancel",
  "md.actions.deactivate": "Deactivate",
  "md.resource.units": "Units",
  "md.resource.productCategories": "Product Categories",
  "md.resource.orderLabels": "Order Labels",
  "md.resource.orderReasons": "Order Reasons",
  "md.resource.shippingZones": "Shipping Zones",
  "md.resource.currencies": "Currencies",
  "md.resource.countryConfigs": "Countries",
  "md.resource.governorates": "Governorates",
  "md.field.name": "Name",
  "md.field.code": "Code",
  "md.field.color": "Color",
  "md.field.kind": "Kind",
  "md.field.countryCode": "Country code",
  "md.field.symbol": "Symbol",
  "md.field.decimalDigits": "Decimal digits",
  "md.field.defaultCurrencyCode": "Default currency",
  "md.field.phoneCode": "Phone code",
  "md.kind.cancellation": "Cancellation",
  "md.kind.return": "Return",
  "md.kind.general": "General",
  "settings.subtitle": "Manage your app settings and data import.",
  "settings.tab.general": "General",
  "settings.tab.dataImport": "Data Import",
  "settings.tab.security": "Security",
  "settings.tab.whatsapp": "WhatsApp",
  "settings.tab.notifications": "Notifications",
  "settings.tab.shipping": "Shipping",
  "settings.tab.storefront": "Storefront",
  "settings.tab.reasons": "Reasons",
  "settings.reasons.cancellationTitle": "Cancellation reasons",
  "settings.reasons.returnTitle": "Return reasons",
  "settings.reasons.addPlaceholder": "Enter a reason…",
  "settings.reasons.add": "Add",
  "settings.reasons.remove": "Remove",
  "settings.reasons.empty": "No reasons added yet.",
  "settings.shipping.zonesTitle": "Shipping zones",
  "settings.shipping.zonesEmpty": "No shipping zones. Add one to set delivery pricing.",
  "settings.shipping.addZone": "Add zone",
  "settings.shipping.carriersTitle": "Shipping carriers",
  "settings.shipping.carriersSubtitle": "Carriers available to create shipments and waybills.",
  "settings.shipping.available": "Available",
  "settings.shipping.connected": "Connected",
  "settings.shipping.notConnected": "Not connected",
  "settings.shipping.connectAction": "Connect",
  "settings.shipping.disconnectAction": "Disconnect",
  "settings.shipping.apiKeyPlaceholder": "API key",
  "settings.shipping.pickupLocationWarning":
    "Connected, but no pickup location is set up in your Bosta account yet.",
  "settings.shipping.connectInvalidKey": "This API key was rejected. Check it and try again.",
  "settings.shipping.connectUnavailable": "Could not reach the carrier. Try again shortly.",
  "settings.shipping.connectFailed": "Could not connect. Please try again.",
  "storefront.forbidden": "You don't have access to storefront integrations.",
  "storefront.title": "Storefront connections",
  "storefront.subtitle":
    "Connect your online store(s) — each connection gets its own API key. Orders and products your store sends in appear automatically.",
  "storefront.empty": "No storefront connections yet.",
  "storefront.loadMore": "Load more",
  "storefront.saved": "Saved.",
  "storefront.saveFailed": "Could not save. Please try again.",
  "storefront.revoked": "Connection revoked.",
  "storefront.rotateFailed": "Could not rotate the key. Please try again.",
  "storefront.revokeFailed": "Could not revoke the connection. Please try again.",
  "storefront.actions.create": "New connection",
  "storefront.actions.edit": "Edit",
  "storefront.actions.rotate": "Rotate key",
  "storefront.actions.revoke": "Revoke",
  "storefront.actions.save": "Save",
  "storefront.actions.cancel": "Cancel",
  "storefront.actions.close": "Close",
  "storefront.actions.viewEvents": "View events",
  "storefront.field.label": "Label",
  "storefront.field.platform": "Platform",
  "storefront.field.apiKeyPrefix": "API key",
  "storefront.field.defaultWarehouse": "Default warehouse",
  "storefront.field.noWarehouse": "No default warehouse",
  "storefront.field.status": "Status",
  "storefront.field.active": "Active (unchecked = paused)",
  "storefront.field.lastEventAt": "Last event",
  "storefront.field.webhookSecret": "Webhook secret",
  "storefront.field.webhookSecretHint":
    "From WooCommerce's own webhook settings. Optional — verified in addition to the API key. Never shown again after saving.",
  "storefront.platform.generic": "Generic (custom store)",
  "storefront.platform.salla": "Salla",
  "storefront.platform.zid": "Zid",
  "storefront.platform.shopify": "Shopify",
  "storefront.platform.woocommerce": "WooCommerce",
  "storefront.status.active": "Active",
  "storefront.status.paused": "Paused",
  "storefront.status.revoked": "Revoked",
  "storefront.create.title": "New storefront connection",
  "storefront.create.labelTaken": "A connection with this label already exists.",
  "storefront.edit.title": "Edit connection",
  "storefront.rotate.confirmTitle": "Rotate this connection's API key?",
  "storefront.rotate.confirmDescription":
    "The current key stops working immediately. Your store must be updated with the new key — it is shown once, right after this.",
  "storefront.revoke.confirmTitle": "Revoke this connection?",
  "storefront.revoke.confirmDescription":
    "This is permanent — the connection's key stops working immediately and cannot be reactivated. Create a new connection to reconnect this store.",
  "storefront.revoke.confirmAction": "Yes, revoke connection",
  "storefront.apiKey.title": "API key — shown once",
  "storefront.apiKey.warning":
    "Copy this key now and store it safely. For security, it cannot be shown again — you would need to rotate the key to get a new one.",
  "storefront.apiKey.copy": "Copy to clipboard",
  "storefront.apiKey.copied": "Copied to clipboard.",
  "storefront.apiKey.copyFailed": "Could not copy. Select and copy the key manually.",
  "storefront.apiKey.ack": "I've copied this key",
  "storefront.events.title": "Recent events",
  "storefront.events.empty": "No events yet for this connection.",
  "storefront.events.filter.status": "Filter by status",
  "storefront.events.filter.allStatuses": "All statuses",
  "storefront.events.filter.eventType": "Filter by type",
  "storefront.events.filter.allTypes": "All types",
  "storefront.events.field.attempts": "Attempts",
  "storefront.events.status.pending": "Pending",
  "storefront.events.status.processed": "Processed",
  "storefront.events.status.failed": "Failed",
  "storefront.events.type.order": "Order",
  "storefront.events.type.product": "Product",
  "storefront.events.reprocess": "Reprocess",
  "storefront.events.reprocessSuccess": "Event reprocessed.",
  "storefront.events.reprocessFailed": "Could not reprocess the event. Please try again.",
  "storefront.vendorWarehouses.title": "Vendor warehouses",
  "storefront.vendorWarehouses.description":
    "Route each marketplace vendor's orders to its own warehouse. A vendor with no mapping here fails the order instead of guessing a warehouse.",
  "storefront.vendorWarehouses.empty": "No vendors mapped yet.",
  "storefront.vendorWarehouses.field.vendorId": "Vendor id",
  "storefront.vendorWarehouses.field.vendorIdHint":
    "The vendor's id as the platform reports it (WooCommerce/WCFM: the vendor's WordPress user id).",
  "storefront.vendorWarehouses.field.warehouse": "Warehouse",
  "storefront.vendorWarehouses.actions.add": "Add mapping",
  "storefront.vendorWarehouses.actions.remove": "Remove",
  "storefront.vendorWarehouses.remove.confirmTitle": "Remove this vendor mapping?",
  "storefront.vendorWarehouses.remove.confirmDescription":
    "Orders from this vendor will fail closed again until it is mapped to a warehouse.",
  "storefront.vendorWarehouses.remove.confirmAction": "Yes, remove mapping",
  "storefront.vendorWarehouses.removed": "Mapping removed.",
  "storefront.vendorWarehouses.removeFailed": "Could not remove the mapping. Please try again.",
  "storefront.vendorWarehouses.addFailed": "Could not add the mapping. Please try again.",
  "storefront.vendorWarehouses.duplicateVendor":
    "This vendor is already mapped on this connection.",
  "settings.general.languageTitle": "Language",
  "settings.general.languageSubtitle": "Choose your preferred language.",
  "settings.general.arabic": "Arabic",
  "settings.general.english": "English",
  "settings.general.subscriptionTitle": "Subscription",
  "settings.general.subscriptionPending": "Subscription and billing details are not available yet.",
  "settings.security.changePasswordTitle": "Change password",
  "settings.security.currentPassword": "Current password",
  "settings.security.newPassword": "New password",
  "settings.security.confirmPassword": "Confirm password",
  "settings.security.updatePassword": "Update password",
  "settings.security.mismatch": "The new password and confirmation do not match.",
  "settings.security.changeSuccess": "Password updated.",
  "settings.security.changeFailed": "Could not update the password. Please try again.",
  "settings.security.wrongPassword": "The current password is incorrect.",
  "settings.security.deleteTitle": "Delete account",
  "settings.security.deleteSubtitle": "Permanently request deletion of your account.",
  "settings.security.deleteAction": "Request account deletion",
  "settings.security.deleteConfirm": "Are you sure? This cannot be undone.",
  "settings.security.deleteConfirmYes": "Yes, request deletion",
  "settings.security.deleteRequested": "Your deletion request has been submitted for review.",
  "settings.security.deleteFailed": "Could not submit the request. Please try again.",
  "settings.dataImport.productsTitle": "Import products",
  "settings.dataImport.productsDescription":
    "Bulk-import products from an Excel/Sheets CSV export. Map name, code and stock-related columns.",
  "settings.dataImport.ordersTitle": "Import orders",
  "settings.dataImport.ordersDescription":
    "Bulk-import previous order data from a CSV or Excel file. Map columns to match your data.",
  "settings.dataImport.csvHint": 'CSV file (.csv). Export from Excel/Sheets as "CSV" first.',
  "settings.dataImport.notMapped": "— Not mapped —",
  "settings.dataImport.startImport": "Start import",
  "settings.dataImport.importing": "Importing…",
  "settings.dataImport.importAnother": "Import another file",
  "settings.dataImport.failed": "The import could not be started. Please try again.",
  "settings.dataImport.summary": "{{succeeded}} imported, {{failed}} failed.",
  "settings.dataImport.rowError": "Row {{row}}: {{message}}",
  "settings.dataImport.description": "Description column",
  "settings.dataImport.categoryId": "Category ID column",
  "settings.dataImport.unitId": "Unit ID column",
  "settings.dataImport.sku": "SKU column",
  "settings.dataImport.barcode": "Barcode column",
  "settings.dataImport.customerId": "Customer ID column",
  "settings.dataImport.variantId": "Variant ID column",
  "settings.dataImport.quantity": "Quantity column",
  "settings.dataImport.price": "Price column (minor units)",
  "settings.dataImport.shippingFee": "Shipping fee column",
  "settings.dataImport.discount": "Discount column",
  "settings.whatsapp.countryCodeTitle": "Country prefix",
  "settings.whatsapp.countryCodeSubtitle":
    "Dialing prefix added before phone numbers (e.g. 20 for Egypt).",
  "settings.whatsapp.templatesTitle": "Message templates",
  "settings.whatsapp.templatesSubtitle": "Reusable templates for WhatsApp conversations.",
  "settings.whatsapp.templateBody": "Template text",
  "settings.whatsapp.addTemplate": "Add template",
  "settings.whatsapp.templatesEmpty": "No templates yet.",
  "customers.title": "Customers",
  "customers.subtitle": "The people who buy from you, their contacts and addresses.",
  "customers.forbidden": "You do not have access to customers.",
  "customers.empty": "No customers yet.",
  "customers.loadMore": "Load more",
  "customers.saved": "Saved",
  "customers.saveFailed": "Could not save. Please try again.",
  "customers.duplicatePhone": "A customer with this phone number already exists.",
  "customers.invalidPhone": "Enter the phone in international format, e.g. +201001234567.",
  "customers.search.label": "Search",
  "customers.search.placeholder": "Full phone number, or a name or email…",
  "customers.search.submit": "Search",
  "customers.search.hint": "A phone must be complete — a partial number matches nothing.",
  "customers.status.title": "Status",
  "customers.status.active": "Active",
  "customers.status.inactive": "Archived",
  "customers.actions.create": "New customer",
  "customers.actions.details": "Details",
  "customers.actions.edit": "Edit",
  "customers.actions.save": "Save",
  "customers.actions.cancel": "Cancel",
  "customers.actions.archive": "Archive",
  "customers.field.name": "Name",
  "customers.field.phone": "Phone",
  "customers.field.phoneFull": "Phone (full)",
  "customers.field.phoneHint": "International format, e.g. +201001234567.",
  "customers.field.phoneEditHint": "Leave blank to keep the current number.",
  "customers.field.email": "Email",
  "customers.field.notes": "Notes",
  "customers.form.sectionBasic": "Basic information",
  "customers.form.sectionAddress": "Address",
  "customers.kpi.orders": "Orders",
  "customers.kpi.spent": "Total spent",
  "customers.kpi.lastOrder": "Last order",
  "customers.detail.title": "Customer details",
  "customers.purchaseHistory.title": "Purchase history",
  "customers.purchaseHistory.empty": "No orders yet.",
  "customers.purchaseHistory.noReview": "No review",
  "customers.addresses.title": "Addresses",
  "customers.addresses.add": "Add address",
  "customers.addresses.edit": "Edit address",
  "customers.addresses.empty": "No addresses yet.",
  "customers.addresses.default": "Default",
  "customers.address.field.line": "Address",
  "customers.address.field.governorate": "Governorate",
  "customers.address.field.area": "District/area",
  "customers.address.field.landmark": "Landmark",
  "customers.address.field.bostaCity": "Governorate",
  "customers.address.field.bostaDistrict": "District",
  "customers.address.field.isDefault": "Default address",
  "customers.address.source.storefront": "Synced from the storefront",
  "products.title": "Products",
  "products.subtitle": "Your catalog of products and their sellable variants.",
  "products.forbidden": "You do not have access to products.",
  "products.empty": "No products yet.",
  "products.loadMore": "Load more",
  "products.saved": "Saved",
  "products.saveFailed": "Could not save. Please try again.",
  "products.search.label": "Search",
  "products.search.placeholder": "Search by name, SKU, or barcode…",
  "products.search.submit": "Search",
  "products.status.title": "Status",
  "products.status.active": "Active",
  "products.status.inactive": "Archived",
  "products.actions.create": "New product",
  "products.actions.edit": "Edit",
  "products.actions.save": "Save",
  "products.actions.cancel": "Cancel",
  "products.actions.archive": "Archive",
  "products.field.name": "Name",
  "products.field.description": "Description",
  "products.field.category": "Category",
  "products.field.unit": "Unit",
  "products.field.image": "Image",
  "products.field.imageHint": "A direct image URL (e.g. from your storefront).",
  "products.field.warehouse": "Warehouse",
  "products.field.warehouseNone": "None",
  "products.field.allowOversell": "Allow selling beyond available stock",
  "products.form.sectionBasic": "Basic information",
  "products.form.sectionVariant": "Variant",
  "products.variants.title": "Variants",
  "products.variants.toggle": "Variants",
  "products.variants.add": "Add variant",
  "products.variants.empty": "No variants yet.",
  "products.variant.field.name": "Name",
  "products.variant.field.sku": "SKU",
  "products.variant.field.barcode": "Barcode",
  "products.variant.field.warehouse": "Warehouse",
  "products.variant.warehouseSaved": "Warehouse updated",
  "products.variant.warehouseFailed": "Could not update warehouse. Please try again.",
  "products.filter.warehouse": "Warehouse",
  "products.filter.clear": "Clear filters",
  "inventory.title": "Inventory",
  "inventory.subtitle": "Stock levels across your locations, and the locations themselves.",
  "inventory.forbidden": "You do not have access to inventory.",
  "inventory.saved": "Saved",
  "inventory.saveFailed": "Could not save. Please try again.",
  "inventory.loadMore": "Load more",
  "inventory.tab.stock": "Stock",
  "inventory.tab.warehouses": "Warehouses",
  "inventory.filter.warehouse": "Warehouse",
  "inventory.filter.lowOnly": "Low stock only",
  "inventory.filter.clear": "Clear filters",
  "inventory.stock.empty": "No stock levels yet.",
  "inventory.stock.low": "Low",
  "inventory.stock.onHand": "On hand",
  "inventory.stock.committed": "Committed",
  "inventory.stock.available": "Available",
  "inventory.stock.reorderPoint": "Reorder point",
  "inventory.warehouses.empty": "No warehouses yet.",
  "inventory.status.title": "Status",
  "inventory.status.active": "Active",
  "inventory.status.inactive": "Archived",
  "inventory.actions.create": "New warehouse",
  "inventory.actions.edit": "Edit",
  "inventory.actions.joinCode": "Join code",
  "inventory.actions.archive": "Archive",
  "inventory.actions.save": "Save",
  "inventory.actions.cancel": "Cancel",
  "inventory.actions.adjust": "Adjust stock",
  "inventory.actions.transfer": "Transfer stock",
  "inventory.actions.setReorderPoint": "Set reorder point",
  "inventory.field.name": "Name",
  "inventory.field.code": "Code",
  "inventory.field.address": "Address",
  "inventory.field.isDefault": "Default location",
  "inventory.field.warehouse": "Warehouse",
  "inventory.field.fromWarehouse": "From warehouse",
  "inventory.field.toWarehouse": "To warehouse",
  "inventory.field.variant": "Variant",
  "inventory.field.image": "Image",
  "inventory.field.product": "Product",
  "inventory.variantPicker.selectWarehouse": "Choose a warehouse first",
  "inventory.variantPicker.loading": "Loading variants…",
  "inventory.variantPicker.empty": "This warehouse holds no variants yet",
  "inventory.variantPicker.error": "Could not load this warehouse's variants",
  "inventory.field.quantity": "Quantity",
  "inventory.field.quantityDelta": "Change (+/−)",
  "inventory.field.reason": "Reason",
  "inventory.field.note": "Note",
  "inventory.reason.count": "Stock count",
  "inventory.reason.damage": "Damage",
  "inventory.reason.loss": "Loss",
  "inventory.reason.return": "Return",
  "inventory.reason.other": "Other",
  "inventory.joinCode.title": "Join code — {{name}}",
  "inventory.joinCode.close": "Close",
  "inventory.joinCode.loadError": "Couldn't load the join code. Please try again.",
  "inventory.joinCode.shownOnce":
    "This code is shown only once. Copy it now and share it with the vendor — you won't be able to see it again.",
  "inventory.joinCode.codeLabel": "Warehouse join code",
  "inventory.joinCode.statusActive": "Active — share it with the vendor to join this warehouse.",
  "inventory.joinCode.statusRevoked": "Revoked. Create a new one to let a vendor join.",
  "inventory.joinCode.statusNone": "No join code yet for this warehouse.",
  "inventory.joinCode.rotate": "Rotate (issue a new code)",
  "inventory.joinCode.create": "Create join code",
  "inventory.joinCode.revoke": "Revoke",

  "orders.title": "Orders",
  "orders.subtitle": "The order lifecycle, from received to closed — status, assignment and COD.",
  "orders.forbidden": "You do not have access to orders.",
  "orders.empty": "No orders here.",
  "orders.saved": "Saved",
  "orders.saveFailed": "Could not save the order.",
  "orders.invalid": "That change is not allowed for this order.",
  "orders.stockShortageLine": "requested {{requested}}, available {{available}}",
  "orders.reasonRequired": "Cancelling needs a reason — open the order to choose one.",
  "orders.cancelModal.title": "Cancel order",
  "orders.cancelModal.reasonLabel": "Cancellation reason",
  "orders.cancelModal.reasonPlaceholder": "Choose a reason",
  "orders.cancelModal.cancel": "Back",
  "orders.cancelModal.confirm": "Cancel order",
  "orders.loadMore": "Load more",
  "orders.board.label": "Orders by status",
  "orders.board.dropHere": "Drop here to move the order to this status",
  "orders.board.empty": "No orders",
  "orders.board.dragHint": "Drag an order onto another status to move it.",
  "orders.board.moved": "Order #{{order}} moved to {{status}}.",
  "orders.board.moveFailed": "Could not move the order.",
  "orders.board.changeStatus": "Change status",
  "orders.board.changeStatusFor": "Change status for order #{{order}}",
  "orders.tabs.all": "All",
  "orders.search.label": "Search",
  "orders.search.placeholder": "Order number or customer name",
  "orders.search.submit": "Search",
  "orders.status.new": "New",
  "orders.status.confirming": "Confirming",
  "orders.status.processing": "Processing",
  "orders.status.incomplete": "Incomplete",
  "orders.status.ready": "Ready",
  "orders.status.shipped": "Shipped",
  "orders.status.delivered": "Delivered",
  "orders.status.completed": "Completed",
  "orders.status.postponed": "Postponed",
  "orders.status.cancelled": "Cancelled",
  "orders.status.returned": "Returned",
  "orders.status.exchanged": "Exchanged",
  "orders.payment.unpaid": "Unpaid",
  "orders.payment.partial": "Partial",
  "orders.payment.paid": "Paid",
  "orders.field.items": "Items",
  "orders.item.notInCrm": "Not in the CRM",
  "orders.item.notInCrmHint":
    "The storefront sold a product the CRM has not received yet. It will complete itself when the product syncs.",
  "orders.field.total": "Total",
  "orders.field.collected": "Collected",
  "orders.field.remaining": "Remaining",
  "orders.field.payment": "Payment",
  "orders.field.orderNumber": "Order #",
  "orders.field.tags": "Tags",
  "orders.field.assigned": "Assigned",
  "orders.field.createdAt": "Created",
  "orders.status.title": "Status",
  "orders.actions.create": "New order",
  "orders.actions.details": "Details",
  "orders.actions.changeStatus": "Status",
  "orders.actions.save": "Save",
  "orders.actions.cancel": "Cancel",
  "orders.actions.rowMenu": "Row actions",
  "orders.actions.transitionTo": "Move to",
  "orders.actions.export": "Export",
  "orders.actions.print": "Print",
  "orders.actions.tags": "Tags",
  "orders.actions.advancedFilters": "Advanced filters",
  "orders.actions.columns": "Columns",
  "orders.actions.more": "More",
  "orders.actions.copyOrderNumber": "Copy order number",
  "orders.actions.copied": "Copied",
  "orders.filters.allPayments": "All payment statuses",
  "orders.filters.dateFrom": "From date",
  "orders.filters.dateTo": "To date",
  "orders.filters.advanced": "Advanced filters",
  "orders.filters.count": "Filters",
  "orders.filters.reset": "Reset",
  "orders.footer.showing": "Showing {{count}} of {{total}}",
  "orders.detail.title": "Order no. {{number}}",
  "orders.detail.tabs.summary": "Summary",
  "orders.detail.tabs.assignee": "Assignee",
  "orders.detail.assignee.unassigned": "Unassigned",
  "orders.detail.assignee.change": "Assign to",
  "orders.detail.tabs.customer": "Customer",
  "orders.detail.tabs.items": "Items",
  "orders.detail.tabs.shipping": "Shipping",
  "orders.detail.tabs.payments": "Payments",
  "orders.detail.tabs.activities": "Activities",
  "orders.detail.tabs.timeline": "Timeline",
  "orders.detail.tabs.notes": "Notes",
  "orders.detail.tabs.vendorTracking": "Vendor tracking",
  "orders.detail.tabs.review": "Review",
  "orders.detail.vendorTracking.noVendor": "No vendor has joined this warehouse yet",
  "orders.detail.vendorTracking.updatedAt": "Updated",
  "orders.detail.vendorTracking.overallStatus": "Overall status",
  "orders.detail.vendorTracking.progress": "{{delivered}} of {{total}} vendors delivered",
  "orders.detail.vendorTracking.itemsCount": "{{count}} item(s)",
  "orders.detail.vendorTracking.allDelivered.title": "All vendors delivered",
  "orders.detail.vendorTracking.allDelivered.description":
    "Every product is ready for the customer to receive from all vendors",
  "orders.detail.loadingPhone": "Loading phone…",
  "orders.detail.delivery.title": "Delivery",
  "orders.detail.delivery.noAddress": "No delivery address on this order.",
  "orders.detail.call": "Call",
  "orders.detail.whatsapp": "WhatsApp",
  "orders.detail.section.financial": "Financial summary",
  "orders.detail.section.orderInfo": "Order info",
  "orders.whatsapp.rowButtonLabel": "Send WhatsApp message",
  "orders.whatsapp.promptTitle": "Send the customer a WhatsApp message about this order?",
  "orders.whatsapp.send": "Send via WhatsApp",
  "orders.whatsapp.invalidPhone": "This customer has no valid WhatsApp number.",
  "orders.whatsapp.messageConfirming":
    "Hello {{customerName}} 👋\nWe'd like to confirm we've received your order #{{orderNumber}} from {{companyName}}.\nCould you confirm it so we can start preparing it?\nThank you for trusting us 🌸",
  "orders.whatsapp.messageReady":
    "Hello {{customerName}} 👋\nGood news — your order #{{orderNumber}} from {{companyName}} is ready and will be shipped soon.\nThank you for choosing us 🌸",
  "orders.whatsapp.messageShipped":
    "Hello {{customerName}} 👋\nYour order #{{orderNumber}} from {{companyName}} has been shipped and should arrive soon, God willing.\nWe hope you enjoy it 🌸",
  "orders.savedViews.all": "All orders",
  "orders.savedViews.mine": "My orders",
  "orders.savedViews.today": "Today",
  "orders.savedViews.late": "Late",
  "orders.savedViews.cancelled": "Cancelled",
  "orders.savedViews.label": "Saved views",
  "orders.grid.sortHint": "Sorts loaded rows only",
  "orders.bulk.selected": "{{count}} selected",
  "orders.bulk.clear": "Clear",
  "orders.mobile.selectedCount": "{{count}} selected",
  "orders.mobile.changeStatusTo": "Change status to",
  "orders.mobile.noSharedStatus": "These orders have no status in common to move to.",
  "orders.mobile.selectOrder": "Select order #{{order}}",
  "orders.bulk.changeStatus": "Change status",
  "orders.bulk.assign": "Assign",
  "orders.form.customer": "Customer",
  "orders.form.variant": "Product / variant",
  "orders.form.quantity": "Qty",
  "orders.form.price": "Price",
  "orders.form.addLine": "Add line",
  "orders.form.addLineHint": "Pick a product first.",
  "orders.form.noLines": "No lines yet.",
  "orders.form.shipping": "Shipping",
  "orders.form.giftWrap": "Gift wrap",
  "orders.form.discount": "Discount",
  "orders.form.warehouse": "Warehouse",
  "orders.form.newCustomer": "New customer",
  "orders.form.saveCustomer": "Save customer",
  "orders.form.customerName": "Name",
  "orders.form.customerPhone": "Phone",
  "orders.form.customerSecondaryPhone": "Secondary phone",
  "orders.form.customerGovernorate": "Governorate",
  "orders.form.customerCity": "City",
  "orders.form.customerStreet": "Street address",
  "orders.form.addressSaveFailed": "The customer was saved, but the address could not be saved.",
  "orders.form.paymentStatus": "Payment status",
  "orders.form.paymentStatus.unpaid": "Unpaid",
  "orders.form.paymentStatus.paid": "Paid",
  "orders.form.paymentStatus.partial": "Partially paid",
  "orders.form.paymentStatus.error": "Paid amount doesn't match the selected payment status.",
  "orders.form.paidAmount": "Paid amount",
  "orders.form.remainingAmount": "Remaining amount",
  "orders.form.notes": "Notes",
  "orders.form.salesChannel": "Order source",
  "orders.form.salesChannelHint": "Where this order came from. Press again to clear.",
  "orders.channel.facebook": "Facebook",
  "orders.channel.whatsapp": "WhatsApp",
  "orders.channel.tiktok": "TikTok",
  "orders.channel.instagram": "Instagram",
  "orders.channel.phone": "Phone call",
  "orders.channel.storefront": "Online store",
  "orders.form.summary.title": "Order summary",
  "orders.form.summary.subtotal": "Subtotal",
  "orders.form.summary.discount": "Discount",
  "orders.form.summary.shipping": "Shipping",
  "orders.form.summary.total": "Grand total",
  "orders.form.header.subtitle": "Add a new order to the system",
  "orders.form.pasteHint": "Paste a customer's chat to pick out the phone number and items",
  "orders.form.pastePlaceholder": "Type or paste the customer's message…",
  "orders.form.warehouseHint": "Choose the warehouse this order ships from",
  "orders.form.customerHint": "Pick a customer or add a new one",
  "orders.form.variantHint": "Choose the product or variant to order",
  "orders.form.variantSearchFailed": "Couldn't load products. Check your connection and try again.",
  "orders.form.currency": "EGP",
  "orders.form.addToCart": "Add to order",
  "orders.form.linesTitle": "Added products",
  "orders.form.lineProduct": "Product",
  "orders.form.lineTotal": "Total",
  "orders.form.lineActions": "Actions",
  "orders.form.removeLine": "Remove {{name}}",
  "orders.form.emptyCartTitle": "No products added yet",
  "orders.form.emptyCartHint": "Pick a product and add it to the order",
  "orders.form.pricingTitle": "Discount & shipping",
  "orders.form.pricingHint": "Add a discount or a shipping fee, if there is one",
  "orders.form.notesPlaceholder": "Any extra notes…",
  "orders.form.saveOrder": "Save order",
  "orders.detail.items": "Items",
  "orders.detail.activity": "Activity",
  "orders.detail.collect": "Collect amount",
  "orders.paste.label": "Smart paste (from a chat)",
  "orders.paste.button": "Detect",
  "orders.paste.detected": "Detected",
  "shipping.forbidden": "You do not have access to shipping.",
  "shipping.section.title": "Shipment",
  "shipping.none": "No shipment yet.",
  "shipping.lastCancelled": "The last shipment was cancelled — create a new one.",
  "shipping.saved": "Saved",
  "shipping.saveFailed": "Could not update the shipment.",
  "shipping.invalid": "That change is not allowed for this shipment.",
  "shipping.field.carrier": "Carrier",
  "shipping.field.tracking": "Tracking #",
  "shipping.field.status": "Status",
  "shipping.field.fee": "Shipping fee",
  "shipping.field.deliveredAt": "Delivered",
  "shipping.status.created": "Created",
  "shipping.status.picked_up": "Picked up",
  "shipping.status.in_transit": "In transit",
  "shipping.status.delivered": "Delivered",
  "shipping.status.returned": "Returned",
  "shipping.status.cancelled": "Cancelled",
  "shipping.actions.create": "Create shipment",
  "shipping.actions.advance": "Advance to",
  "shipping.actions.cancel": "Cancel shipment",
  "shipping.actions.waybill": "Waybill",
  "shipping.waybillIssued": "Waybill issued",
  "shipping.cancelSuccess": "Shipment cancelled successfully.",
  "shipping.confirmCancel.title": "Cancel shipment",
  "shipping.confirmCancel.message":
    "Are you sure you want to cancel this shipment? This action cannot be undone.",
  "review.section.title": "Customer review",
  "review.none": "No review yet.",
  "review.actions.add": "Add review",
  "review.actions.save": "Save review",
  "review.actions.cancel": "Cancel",
  "review.saved": "Review saved.",
  "review.saveFailed": "Could not save the review.",
  "review.field.productType": "Product type",
  "review.productType.clothes": "Clothes",
  "review.productType.electronics": "Electronics",
  "review.productType.gifts": "Gifts",
  "review.field.giftRecipientName": "Recipient's name",
  "review.field.giftRecipientRelation": "Relation to the customer",
  "review.field.giftOccasion": "Occasion",
  "review.field.quality": "Quality",
  "review.field.packaging": "Packaging",
  "review.field.shipping": "Shipping",
  "review.field.lowReason": "Reason for the low rating",
  "review.field.average": "Overall average",
  "shipping.confirmCancel.confirm": "Confirm cancellation",
  "shipping.confirmCancel.cancel": "Back",
  "shipping.orderNotReady":
    'A shipment cannot be created because the order is still "{{status}}". Move the order to "Ready" first.',
  "shipping.orderNotReady.action": "Change order status",
  "shipping.selectCarrier.title": "Select a shipping carrier",
  "shipping.selectCarrier.field": "Shipping company",
  "shipping.selectCarrier.storefrontHint":
    "From the storefront (pick the matching Bosta city/district)",
  "shipping.selectCarrier.none": "No shipping carrier is connected yet.",
  "shipping.selectCarrier.goToSettings": "Go to shipping settings",
  "shipping.selectCarrier.continue": "Continue",
  "shipping.selectCarrier.cancel": "Cancel",
  "shipping.selectCarrier.goodsValue": "Goods value (optional)",
  "shipping.selectCarrier.zone": "Zone",
  "shipping.selectCarrier.addressLine": "Address",
  "shipping.selectCarrier.addressLine.placeholder": "Street, building, floor, apartment",
  "shipping.selectCarrier.landmark": "Landmark",
  "shipping.selectCarrier.landmark.placeholder": "A nearby well-known spot",
  "shipping.selectCarrier.recipient": "Recipient",
  "shipping.selectCarrier.recipientFirstName": "First name",
  "shipping.selectCarrier.recipientLastName": "Last name",
  "shipping.selectCarrier.recipientPhone2": "Second phone (optional)",
  "shipping.selectCarrier.allowOpenPackage":
    "Allow the customer to open the package before accepting it",
  "shipping.addressMissing.message":
    "Fill in the delivery address above before creating the shipment.",
  "shipping.addressMissing.editCustomer": "Edit customer details",
  "customers.shippingInfo.title": "Shipping information",

  "nav.finance": "Finance",
  "finance.title": "Finance",
  "finance.subtitle": "Expenses, purchase orders, the cash center & P&L, and suppliers.",
  "finance.hero.breadcrumb": "Breadcrumb",
  "finance.hero.home": "Home",
  "finance.hero.badgeTitle": "A clearer financial picture",
  "finance.hero.badgeHint": "Easier management for your business",
  "finance.forbidden": "You do not have access to finance.",
  "finance.saved": "Saved",
  "finance.saveFailed": "Could not save. Please try again.",
  "finance.loadMore": "Load more",
  "finance.tab.suppliers": "Suppliers",
  "finance.tab.purchaseOrders": "Purchase orders",
  "finance.tab.expenses": "Expenses",
  "finance.tab.reports": "Cash center & P&L",
  "finance.actions.save": "Save",
  "finance.actions.cancel": "Cancel",
  "finance.actions.create": "New",
  "finance.actions.edit": "Edit",
  "finance.actions.archive": "Archive",
  "finance.actions.view": "View",
  "finance.actions.close": "Close",
  "finance.status.active": "Active",
  "finance.status.inactive": "Archived",
  "finance.suppliers.empty": "No suppliers yet.",
  "finance.suppliers.search.placeholder": "Search by name…",
  "finance.suppliers.filter.activeOnly": "Active only",
  "finance.suppliers.field.name": "Name",
  "finance.suppliers.field.phone": "Phone",
  "finance.suppliers.field.email": "Email",
  "finance.suppliers.field.address": "Address",
  "finance.suppliers.field.taxId": "Tax ID",
  "finance.po.empty": "No purchase orders yet.",
  "finance.po.filter.status": "Status",
  "finance.po.filter.clear": "Clear filters",
  "finance.po.filter.supplier": "Supplier",
  "finance.po.field.supplier": "Supplier",
  "finance.po.field.expectedDate": "Expected date",
  "finance.po.field.notes": "Notes",
  "finance.po.field.number": "PO number",
  "finance.po.field.status": "Status",
  "finance.po.field.variant": "Variant",
  "finance.po.field.quantity": "Quantity",
  "finance.po.field.unitCost": "Unit cost",
  "finance.po.form.addLine": "Add line",
  "finance.po.status.draft": "Draft",
  "finance.po.status.ordered": "Ordered",
  "finance.po.status.partially_received": "Partially received",
  "finance.po.status.received": "Received",
  "finance.po.status.cancelled": "Cancelled",
  "finance.po.detail.lines": "Lines",
  "finance.po.actions.receive": "Receive",
  "finance.po.actions.pay": "Record payment",
  "finance.po.receive.warehouse": "Warehouse",
  "finance.po.receive.note":
    "Receives the full remaining quantity of every line into this warehouse.",
  "finance.po.receive.submit": "Confirm receipt",
  "finance.po.pay.amount": "Amount",
  "finance.po.pay.method": "Method",
  "finance.po.pay.submit": "Record payment",
  "finance.expenses.empty": "No expenses yet.",
  "finance.expenses.field.category": "Category",
  "finance.expenses.field.amount": "Amount",
  "finance.expenses.field.incurredAt": "Date",
  "finance.expenses.field.notes": "Notes",
  "finance.expenses.field.supplier": "Supplier",
  "finance.expenses.filter.category": "Category",
  "finance.expenses.currency": "EGP",
  "finance.expenses.listTitle": "Expense records",
  "finance.expenses.add": "Add expense",
  "finance.expenses.addTitle": "New expense",
  "finance.expenses.editTitle": "Edit expense",
  "finance.expenses.editFor": "Edit {{category}} expense",
  "finance.expenses.statsError": "Statistics could not be loaded.",
  "finance.expenses.stats.topCategory": "Top expense category",
  "finance.expenses.stats.topCategoryShare": "of total expenses",
  "finance.expenses.stats.count": "Number of expenses",
  "finance.expenses.stats.total": "Total expenses",
  "finance.expenses.stats.average": "Average monthly expenses",
  "finance.expenses.stats.averageHint": "Total ÷ months elapsed",
  "finance.expenses.stats.vsPrevious": "vs the same period last year",
  "finance.expenses.stats.none": "No expenses",
  "finance.expenses.chart.monthly": "Monthly expenses",
  "finance.expenses.chart.monthlyHint": "Total expenses for each month",
  "finance.expenses.chart.byCategory": "Expenses by category",
  "finance.expenses.chart.byCategoryHint": "Each category's share of the total",
  "finance.expenses.chart.year": "Year",
  "finance.expenses.chart.refresh": "Refresh",
  "finance.expenses.chart.totalLabel": "Total expenses",
  "finance.expenses.chart.empty": "No expenses in {{year}}.",
  "finance.expenses.chart.others": "Other",
  "finance.reports.dateFrom": "From",
  "finance.reports.dateTo": "To",
  "finance.reports.compareFrom": "Compare from",
  "finance.reports.compareTo": "Compare to",
  "finance.reports.actions.load": "Load",
  "finance.reports.cashCenter.title": "Cash center",
  "finance.reports.cashCenter.collected": "Collected",
  "finance.reports.cashCenter.expenses": "Expenses",
  "finance.reports.cashCenter.poPayments": "Purchase order payments",
  "finance.reports.cashCenter.refunds": "Refunds",
  "finance.reports.cashCenter.shippingFees": "Shipping fees",
  "finance.reports.cashCenter.net": "Net cash",
  "finance.reports.pnl.title": "Profit & loss",
  "finance.reports.pnl.revenue": "Revenue",
  "finance.reports.pnl.cogs": "COGS",
  "finance.reports.pnl.expenses": "Expenses",
  "finance.reports.pnl.netIncome": "Net income",
  "finance.reports.pnl.previous": "Previous period",

  "nav.analytics": "Analytics",
  "analytics.title": "Analytics",
  "analytics.subtitle": "Computed views over your data — no forecasting, just real numbers.",
  "analytics.forbidden": "You do not have access to analytics.",
  "analytics.loadFailed": "Could not load. Please try again.",
  "analytics.currency": "EGP",
  "analytics.vsPrevious": "vs. the previous period",
  "analytics.tab.products": "Products",
  "analytics.tab.profitability": "Profitability",
  "analytics.hero.home": "Home",
  "analytics.hero.breadcrumb": "Breadcrumb",
  "analytics.hero.title.products": "Analytics",
  "analytics.hero.title.profitability": "Profitability analysis",
  "analytics.hero.subtitle.products":
    "See which products earn and which lag — decisions built on real numbers.",
  "analytics.hero.subtitle.profitability":
    "Where profit and margin stand against sales and spending over the period.",
  "analytics.hero.badgeTitle.products": "Numbers that sharpen decisions",
  "analytics.hero.badgeTitle.profitability": "Clearer figures, better calls",
  "analytics.hero.badgeHint": "Everything here is computed from your own data.",
  "analytics.window.from": "From",
  "analytics.window.to": "To",
  "analytics.window.granularity": "Group by",
  "analytics.window.granularity.day": "Day",
  "analytics.window.granularity.week": "Week",
  "analytics.window.granularity.month": "Month",
  "analytics.actions.export": "Export CSV",
  "analytics.export.done": "Export downloaded",
  "analytics.export.failed": "Could not export. Please try again.",
  "analytics.business.orderCount": "Orders",
  "analytics.business.collected": "Collected",
  "analytics.business.averageOrderValue": "Average order value",
  "analytics.products.top": "Top performers",
  "analytics.products.bottom": "Lowest performers",
  "analytics.products.topHint": "The best-selling products over the chosen period.",
  "analytics.products.bottomHint": "The slowest-selling products over the chosen period.",
  "analytics.products.product": "Product",
  "analytics.products.unitsSold": "Units sold",
  "analytics.products.unitsShort": "Units",
  "analytics.products.revenue": "Revenue",
  "analytics.products.revenueWithUnit": "Revenue ({{unit}})",
  "analytics.products.count": "{{count}} products",
  "analytics.products.viewAll": "View all products",
  "analytics.products.empty": "Nothing sold in this period.",
  "analytics.products.totalProducts": "Total products",
  "analytics.products.newInPeriod": "+{{count}} new this period",
  "analytics.products.totalRevenue": "Total revenue",
  "analytics.products.averagePrice": "Average product price",
  "analytics.profitability.sales": "Total sales",
  "analytics.profitability.cogs": "Cost of goods sold",
  "analytics.profitability.expenses": "Total expenses",
  "analytics.profitability.netIncome": "Net profit",
  "analytics.profitability.margin": "Profit margin",
  "analytics.profitability.chart.title": "Sales, expenses and profit",
  "analytics.profitability.chart.hint": "Each period in the window, side by side.",
  "analytics.profitability.chart.empty": "No movement in this period.",
  "analytics.profitability.split.title": "How the money divides",
  "analytics.profitability.split.total": "Total movement",
  "analytics.profitability.indicators.title": "Key indicators",
  "analytics.profitability.indicators.hint": "Readings derived from the period's figures.",
  "analytics.profitability.indicators.monthlyExpenses": "Average monthly expenses",
  "analytics.profitability.indicators.monthlyProfit": "Average monthly profit",
  "analytics.profitability.indicators.perMonth": "Per month across the period",
  "analytics.profitability.indicators.netMargin": "Net profit margin",
  "analytics.profitability.indicators.netMarginHint": "Net profit ÷ sales",
  "analytics.profitability.indicators.breakEven": "Break-even point",
  "analytics.profitability.indicators.breakEvenHint": "Sales needed to cover expenses",
  "notifications.bell.label": "Notifications",
  "notifications.title": "Notifications",
  "notifications.empty.title": "No notifications yet",
  "notifications.empty.description": "You'll see updates about your orders here.",
  "notifications.loadFailed": "Could not load notifications.",
  "notifications.markAllRead": "Mark all read",
  "notifications.preferences.link": "Preferences",
  "notifications.type.order.status_changed": "Order status",
  "notifications.type.payment.collected": "Payment collected",
  "notifications.type.order_vendor_group.assigned": "New vendor order assigned",
  "notifications.type.order.created": "New order",
  // The in-app notification sentences. The API stores a stable type + a
  // structured payload (customer, order number, amounts); these templates turn
  // that into the reader's language. The row's own title/body are only the
  // Web Push fallback.
  "notifications.content.orderCreated.title": "New order",
  "notifications.content.orderCreated.body": "Order #{{orderNumber}} from {{customerName}}.",
  "notifications.content.orderCreated.bodyWithTotal":
    "Order #{{orderNumber}} from {{customerName}} for {{total}}.",
  "notifications.content.orderStatusChanged.title": "Order status updated",
  "notifications.content.orderStatusChanged.body":
    "{{customerName}}'s order #{{orderNumber}} is now {{status}}.",
  "notifications.content.paymentCollected.title": "Payment collected",
  "notifications.content.paymentCollected.body":
    "Collected {{amount}} on {{customerName}}'s order #{{orderNumber}}.",
  "notifications.content.vendorGroupAssigned.title": "New order to prepare",
  "notifications.content.vendorGroupAssigned.body":
    "You have a new order to prepare: #{{orderNumber}}.",
  "notifications.type.message.received": "New message",
  "notifications.content.messageReceived.title": "New message",
  "notifications.content.messageReceived.body": "{{preview}}",
  "notifications.content.messageReceived.bodyNoPreview": "You have a new message.",
  "notifications.preferences.title": "Notification preferences",
  "notifications.preferences.subtitle": "Choose how you want to hear about each type of update.",
  "notifications.preferences.inApp": "In-app",
  "notifications.preferences.webPush": "Push notifications",
  "notifications.push.title": "Push on this device",
  "notifications.push.enable": "Enable notifications",
  "notifications.push.disable": "Turn off on this device",
  "notifications.push.later": "Not now",
  "notifications.push.enabled": "Push notifications are on for this device.",
  "notifications.push.disabled": "Push notifications are off for this device.",
  "notifications.push.denied":
    "Your browser is blocking notifications. Allow them in its site settings, then try again.",
  "notifications.push.failed": "Could not turn notifications on. Please try again.",
  "notifications.push.onHint": "This device receives notifications even when the app is closed.",
  "notifications.push.offHint":
    "Turn this on to get notifications on this device even when the app is closed.",
  "notifications.push.blocked":
    "Notifications are blocked for this site. Allow them in your browser's site settings to turn them on.",
  "notifications.push.unsupported":
    "This browser cannot show push notifications. On iPhone, add the app to your Home Screen first.",
  "notifications.push.nudgeTitle": "Never miss an order",
  "notifications.push.nudgeBody": "Get notified on this device even when the app is closed.",
  "notifications.preferences.save": "Save",
  "notifications.preferences.saved": "Preferences saved.",
  "notifications.preferences.saveFailed": "Could not save preferences. Please try again.",

  "onboarding.start.title": "Get started",
  "onboarding.start.subtitle": "Create a new company or join an existing one",
  "onboarding.start.create.title": "Create a company",
  "onboarding.start.create.description": "Set up a new workspace for your company",
  "onboarding.start.join.title": "Join a company",
  "onboarding.start.join.description": "Enter an invite code to join a company",

  "onboarding.create.title": "Create a company",
  "onboarding.create.field.name": "Company name",
  "onboarding.create.field.country": "Country",
  "onboarding.create.field.countryPlaceholder": "Select a country",
  "onboarding.create.field.phone": "Phone number",
  "onboarding.create.field.monthlyOrdersRange": "Monthly orders",
  "onboarding.create.field.monthlyOrdersRangePlaceholder": "Select monthly orders",
  "onboarding.create.field.hasFacebook": "Do you have a Facebook page?",
  "onboarding.create.field.facebookHandle": "Facebook page link or name",
  "onboarding.create.field.hasInstagram": "Do you have an Instagram account?",
  "onboarding.create.field.instagramHandle": "Instagram link or name",
  "onboarding.create.field.hasWebsite": "Do you have a website?",
  "onboarding.create.field.websiteUrl": "Website URL",
  "onboarding.create.field.shippingCarrier": "Shipping carrier",
  "onboarding.create.field.shippingCarrierPlaceholder": "e.g. DHL, FedEx, Aramex",
  "onboarding.create.submit": "Create company",
  "onboarding.monthlyOrders.under_100": "Under 100",
  "onboarding.monthlyOrders.100_500": "100 - 500",
  "onboarding.monthlyOrders.500_1000": "500 - 1,000",
  "onboarding.monthlyOrders.1000_2000": "1,000 - 2,000",
  "onboarding.monthlyOrders.2000_5000": "2,000 - 5,000",
  "onboarding.monthlyOrders.over_5000": "Over 5,000",

  "onboarding.join.title": "Join a company",
  "onboarding.join.kind.label": "Join with",
  "onboarding.join.kind.invitation": "Company invitation",
  "onboarding.join.kind.warehouseCode": "Vendor / warehouse code",
  "onboarding.join.field.code": "Invite code",
  "onboarding.join.field.warehouseCode": "Warehouse join code",
  "onboarding.join.submit": "Join",
  "onboarding.error.generic": "Something went wrong. Please try again.",
  "onboarding.error.invalidInvitation": "That invite code is invalid or has expired.",
  "onboarding.error.invalidWarehouseCode":
    "That warehouse join code is invalid or has been revoked.",
  "onboarding.country.eg": "Egypt",
  "onboarding.country.sa": "Saudi Arabia",
  "onboarding.country.ae": "United Arab Emirates",
  "onboarding.country.kw": "Kuwait",
  "onboarding.country.qa": "Qatar",
  "onboarding.country.bh": "Bahrain",
  "onboarding.country.om": "Oman",
  "onboarding.country.jo": "Jordan",
  "onboarding.country.iq": "Iraq",
  "onboarding.country.other": "Other",

  "vendor.dashboard.title": "Vendor account",
  "vendor.dashboard.myOrders": "My orders",
  "vendor.dashboard.subtitle": "Only the products routed to your own warehouse.",
  "vendor.dashboard.empty": "No orders yet.",
  "vendor.dashboard.order": "Order",
  "vendor.dashboard.saved": "Saved.",
  "vendor.dashboard.saveFailed": "Couldn't save. Please try again.",
  "vendor.dashboard.updatedAt": "Updated",
  "vendor.dashboard.section.active": "Active",
  "vendor.dashboard.section.delivered": "Delivered",
  "vendor.dashboard.advanceTo.processing": "Start processing",
  "vendor.dashboard.advanceTo.ready": "Mark ready",
  "vendor.dashboard.advanceTo.delivered": "Mark delivered",
  "vendor.group.status.new": "New",
  "vendor.group.status.processing": "Processing",
  "vendor.group.status.ready": "Ready",
  "vendor.group.status.delivered": "Delivered",

  "orders.vendorGroups.setStatus": "Change status",
  "orders.vendorGroups.overrideHint": "Managers can correct a vendor's status in either direction.",
  "orders.vendorGroups.confirmBack.title": "Move this vendor group backward?",
  "orders.vendorGroups.confirmBack.description":
    "The vendor already marked it {{from}}. Moving it back to {{to}} is recorded against your name.",
  "orders.vendorGroups.confirmBack.yes": "Yes, move it back",
  "orders.vendorGroups.confirmBack.no": "Keep it as is",
  "orders.vendorGroups.moved": "Vendor group moved to {{status}}.",
  "orders.vendorGroups.moveFailed": "Couldn't change the status. Please try again.",

  "vendor.nav.label": "Vendor navigation",
  "vendor.nav.home": "Home",
  "vendor.nav.orders": "Orders",
  "vendor.nav.products": "Products",

  "vendor.dashboard.overview.title": "Overview",
  "vendor.dashboard.kpi.total": "Total orders",
  "vendor.dashboard.kpi.new": "New",
  "vendor.dashboard.kpi.processing": "Processing",
  "vendor.dashboard.kpi.ready": "Ready",
  "vendor.dashboard.kpi.delivered": "Delivered",
  "vendor.dashboard.recent.title": "Recent orders",
  "vendor.dashboard.recent.viewAll": "View all orders",

  "vendor.orders.search.placeholder": "Search by order number",
  "vendor.orders.tabs.all": "All",
  "vendor.orders.sort.newestFirst": "Newest first",
  "vendor.orders.sort.oldestFirst": "Oldest first",

  "vendor.orderDetail.back": "Back to orders",
  "vendor.orderDetail.notFound.title": "Order not found",
  "vendor.orderDetail.notFound.description":
    "This order isn't assigned to your warehouse, or doesn't exist.",
  "vendor.orderDetail.field.warehouse": "Warehouse",
  "vendor.orderDetail.items.title": "My items",
  "vendor.orderDetail.status.title": "Status",

  "vendor.status.more": "Move to a later status",
  "vendor.orders.dragHint": "Tip: drag an order onto a status to move it there.",
  "vendor.orders.dropHere": "Drop to move here",
  "vendor.orders.board.empty": "No orders here",
  "vendor.orders.moved": "Order #{{order}} moved to {{status}}.",
  "vendor.orders.moveFailed": "Couldn't move the order. It's back where it was.",
  "vendor.orders.panelClose": "Close order",

  "vendor.products.title": "My products",
  "vendor.products.subtitle": "Only the products stocked in your own warehouse.",
  "vendor.products.empty": "No products in your warehouse yet.",
  "vendor.products.available": "Available",

  "nav.messages": "Messages",
  "nav.desc.messages": "Chat with your vendors",
  "vendor.nav.messages": "Messages",
  "vendor.messages.title": "Messages",
  "vendor.messages.subtitle": "Your conversation with the company's team.",

  "messaging.title": "Messages",
  "messaging.subtitle": "Conversations with your vendors.",
  "messaging.forbidden": "You don't have access to messaging.",
  "messaging.actions.new": "New conversation",
  "messaging.actions.loadMore": "Load more",
  "messaging.empty.title": "No conversations yet",
  "messaging.empty.description": "Start a conversation with a vendor to see it here.",
  "messaging.thread.close": "Close conversation",
  "messaging.thread.empty": "No messages yet. Say hello!",
  "messaging.thread.loadOlder": "Load earlier messages",
  "messaging.thread.archived": "This conversation is archived.",
  "messaging.thread.noMessagesYet": "No messages yet",
  "messaging.message.deleted": "This message was deleted.",
  "messaging.lightbox.close": "Close",
  "messaging.lightbox.previous": "Previous image",
  "messaging.lightbox.next": "Next image",
  "messaging.composer.placeholder": "Type a message… use @ to mention an order",
  "messaging.composer.attach": "Attach an image",
  "messaging.composer.send": "Send",
  "messaging.composer.sendFailed": "Couldn't send the message. Please try again.",
  "messaging.composer.attachFailed": "Couldn't upload the image. Please try again.",
  "messaging.composer.attachmentsUnavailable": "Image uploads are temporarily unavailable.",
  "messaging.composer.removeMention": "Remove mention",
  "messaging.composer.removeAttachment": "Remove image",
  "messaging.mention.empty": "No matching orders",
  "messaging.picker.title": "Start a conversation",
  "messaging.picker.placeholder": "Choose a vendor",
  "messaging.picker.cancel": "Cancel",
  "messaging.picker.start": "Start",
  "messaging.picker.loadFailed": "Couldn't load vendors. Please try again.",
  "messaging.picker.openFailed": "Couldn't open the conversation. Please try again.",
} as const;

export type TranslationKey = keyof typeof en;

export const ar: Record<TranslationKey, string> = {
  "app.name": "كادو CRM",
  "app.tagline": "نظام CRM آمن ومتعدد المستأجرين للتجارة الاجتماعية",
  "nav.primary": "التنقّل الرئيسي",
  "nav.bottom": "التنقّل السفلي",
  "nav.more": "المزيد",
  "nav.back": "رجوع",
  "nav.desc.dashboard": "نظرة سريعة على نشاط اليوم",
  "nav.desc.orders": "متابعة الطلبات وتجهيزها",
  "nav.desc.customers": "جهات الاتصال والسجل والتصنيفات",
  "nav.desc.products": "الكتالوج والأسعار والمتغيّرات",
  "nav.desc.inventory": "المخازن والأرصدة والتحويلات",
  "nav.desc.finance": "المصروفات وأوامر الشراء والتقارير",
  "nav.desc.analytics": "الربحية وأداء المنتجات",
  "nav.desc.masterData": "القوائم المشتركة التي تغذّي كل الشاشات",
  "nav.desc.team": "الأعضاء والأدوار والصلاحيات",
  "nav.desc.settings": "الشركة والفواتير والتفضيلات",
  "nav.desc.admin": "المستأجرون والخصائص على مستوى المنصّة",
  "nav.menu.darkMode": "الوضع الليلي",
  "nav.dashboard": "لوحة التحكم",
  "nav.orders": "الطلبات",
  "nav.customers": "العملاء",
  "nav.products": "المنتجات",
  "nav.inventory": "المخزون",
  "nav.settings": "الإعدادات",
  "sidebar.collapse": "طيّ القائمة الجانبية",
  "sidebar.expand": "توسيع القائمة الجانبية",
  "sidebar.section.core": "البيانات الأساسية",
  "user.account": "الحساب",
  "user.badge.premium": "مستخدم مميز",
  "user.profile": "الملف الشخصي",
  "user.settings": "الإعدادات",
  "user.signOut": "تسجيل الخروج",
  "placeholder.description": "هذا القسم جزء من Epic لاحق ولم يُبنَ بعد.",
  "command.trigger": "بحث…",
  "command.placeholder": "اكتب أمرًا أو ابحث…",
  "command.empty": "لا توجد نتائج.",
  "command.navigation": "التنقّل",
  "command.actions": "الإجراءات",
  "command.switchLanguage": "تبديل اللغة",
  "combobox.placeholder": "اختر…",
  "combobox.search": "ابحث…",
  "combobox.empty": "لا توجد نتائج.",
  "combobox.loading": "جارٍ البحث…",
  "datePicker.placeholder": "اختر تاريخًا",
  "datePicker.clear": "مسح",
  "form.optional": "اختياري",
  "actions.toggleTheme": "تبديل السمة",
  "filters.title": "الفلاتر",
  "actions.toggleLanguage": "English",
  "install.action": "تثبيت التطبيق",
  "install.help.title": "تثبيت التطبيق على جهازك",
  "install.help.close": "تم",
  "install.help.ios.intro": "من متصفح Safari، أضف Cadeau CRM إلى الشاشة الرئيسية:",
  "install.help.ios.step1": "اضغط زر المشاركة في شريط المتصفح.",
  "install.help.ios.step2": "اختر «إضافة إلى الشاشة الرئيسية».",
  "install.help.ios.step3": "اضغط «إضافة» — وسيظهر التطبيق مع باقي تطبيقاتك.",
  "states.loading": "جارٍ التحميل…",
  "states.empty.title": "لا يوجد شيء بعد",
  "states.empty.description": "عند توفّر بيانات لعرضها ستظهر هنا.",
  "states.error.title": "حدث خطأ ما",
  "states.error.description": "تعذّر إكمال الطلب. يُرجى المحاولة مرة أخرى.",
  "states.error.retry": "إعادة المحاولة",
  "states.offline": "أنت غير متصل — لن تُحمَّل البيانات حتى يعود الاتصال.",
  "dashboard.title": "لوحة التحكم",
  "dashboard.subtitle": "نظرة سريعة على أعمال اليوم.",
  "dashboard.kpi.title": "أرقام أساسية",
  "dashboard.kpi.period": "الفترة",
  "dashboard.kpi.period.today": "اليوم",
  "dashboard.kpi.period.month": "هذا الشهر",
  "dashboard.kpi.period.lastMonth": "الشهر الماضي",
  "dashboard.kpi.period.quarter": "٣ شهور",
  "dashboard.kpi.period.year": "سنة",
  "dashboard.kpi.period.all": "كل الفترات",
  "dashboard.kpi.vs.today": "من أمس",
  "dashboard.kpi.vs.month": "من الشهر السابق",
  "dashboard.kpi.vs.lastMonth": "من الشهر الأسبق",
  "dashboard.kpi.vs.quarter": "من ٣ شهور",
  "dashboard.kpi.vs.year": "من السنة السابقة",
  "dashboard.kpi.vs.all": "كل الفترات",
  "dashboard.kpi.expectedRevenue": "الإيرادات المتوقعة",
  "dashboard.kpi.averageOrder": "متوسط الطلب",
  "dashboard.kpi.shipped": "تم الشحن",
  "dashboard.kpi.processing": "قيد التنفيذ",
  "dashboard.kpi.ready": "الجاهز",
  "dashboard.loadFailed": "تعذّر تحميل هذا العنصر.",
  "dashboard.kpi.orders": "الطلبات",
  "dashboard.quickActions.newOrder": "طلب جديد",
  "dashboard.quickActions.newCustomer": "عميل جديد",
  "dashboard.quickActions.newProduct": "منتج جديد",
  "dashboard.sales.title": "المبيعات",
  "dashboard.statusChart.title": "الطلبات حسب الحالة",
  "dashboard.recentOrders.title": "أحدث الطلبات",
  "dashboard.activity.title": "أحدث النشاطات",
  "dashboard.activity.line": "الطلب رقم {{order}} ← {{status}}",
  "dashboard.lowStock.title": "تنبيهات نقص المخزون",
  "dashboard.lowStock.empty": "لا توجد عناصر منخفضة المخزون حاليًا.",
  "dashboard.notifications.title": "الإشعارات",
  "dashboard.notifications.unread": "{{count}} غير مقروءة",
  "notFound.title": "الصفحة غير موجودة",
  "notFound.description": "الصفحة التي تبحث عنها غير موجودة.",
  "notFound.back": "العودة إلى الرئيسية",
  "auth.login.title": "تسجيل الدخول",
  "auth.login.subtitle": "سجّل الدخول إلى حسابك في كادو CRM.",
  "auth.login.submit": "تسجيل الدخول",
  "auth.login.noAccount": "ليس لديك حساب؟",
  "auth.login.toRegister": "أنشئ حسابًا",
  "auth.register.title": "إنشاء حساب",
  "auth.register.subtitle": "ابدأ استخدام كادو CRM.",
  "auth.register.submit": "إنشاء الحساب",
  "auth.register.haveAccount": "لديك حساب بالفعل؟",
  "auth.register.toLogin": "تسجيل الدخول",
  "auth.field.email": "البريد الإلكتروني",
  "auth.field.password": "كلمة المرور",
  "auth.field.fullName": "الاسم الكامل",
  "auth.field.phone": "الهاتف",
  "auth.field.optional": "اختياري",
  "auth.field.totp": "رمز المصادقة",
  "auth.twoFactor.title": "التحقق بخطوتين",
  "auth.twoFactor.subtitle": "أدخل الرمز المكوّن من 6 أرقام من تطبيق المصادقة.",
  "auth.twoFactor.submit": "تحقّق",
  "auth.twoFactor.back": "رجوع",
  "auth.submitting": "يُرجى الانتظار…",
  "auth.error.invalidCredentials": "البريد الإلكتروني أو كلمة المرور غير صحيحة.",
  "auth.error.invalidCode": "الرمز غير صحيح أو منتهي الصلاحية.",
  "auth.error.emailTaken": "يوجد حساب بهذا البريد الإلكتروني بالفعل.",
  "auth.error.validation": "يُرجى مراجعة الحقول المميّزة.",
  "auth.invalid.email": "اكتب بريدًا إلكترونيًا صحيحًا.",
  "auth.invalid.password": "كلمة المرور لا تقل عن 8 حروف.",
  "auth.invalid.fullName": "الاسم طويل جدًا (200 حرف على الأكثر).",
  "auth.invalid.phone": "استخدم أرقامًا إنجليزية فقط، مثال: 01001234567.",
  "auth.error.rateLimited": "محاولات كثيرة جدًا. حاول مرة أخرى لاحقًا.",
  "auth.error.generic": "حدث خطأ ما. يُرجى المحاولة مرة أخرى.",
  "company.switcher.label": "الشركة النشطة",
  "company.switcher.none": "لا توجد شركة بعد",
  "company.switcher.switch": "تبديل الشركة",
  "nav.admin": "إدارة المنصّة",
  "nav.team": "فريق العمل",
  "team.title": "فريق العمل",
  "team.subtitle": "إدارة الأدوار والصلاحيات وأعضاء فريقك.",
  "team.hero.badge": "إدارة مرنة وآمنة للصلاحيات",
  "team.hero.badgeHint": "كل عضو يرى فقط ما يسمح به دوره.",
  "team.roles.title": "الأدوار والصلاحيات",
  "team.roles.search": "البحث في الأدوار…",
  "team.sections.label": "أقسام فريق العمل",
  "team.sections.rolesDesc": "حدّد ما يستطيع كل دور القيام به",
  "team.sections.membersDesc": "الأعضاء والدعوات المعلّقة",
  "team.sections.rolesCount": "{{count}} أدوار",
  "team.sections.membersCount": "{{count}} أعضاء",
  "team.sections.pendingCount": "{{count}} دعوة معلّقة",
  "team.members.search": "البحث في الأعضاء…",
  "team.members.noMatch": "لا يوجد أعضاء مطابقون للبحث.",
  "team.vendors.title": "حسابات التجار",
  "team.vendors.empty": "لا توجد حسابات تجار بعد.",
  "team.roles.add": "إضافة دور جديد",
  "team.roles.edit": "تعديل الدور",
  "team.roles.viewAll": "عرض جميع الصلاحيات",
  "team.roles.topPermissions": "أهم الصلاحيات",
  "team.roles.more": "+{{count}} أخرى",
  "team.roles.permissionCount": "{{count}} صلاحيات",
  "team.roles.memberCount": "{{count}} مستخدمين",
  "team.roles.actionsFor": "إجراءات {{role}}",
  "team.roles.empty": "لا توجد أدوار معرّفة بعد.",
  "team.roles.noMatch": "لا توجد أدوار مطابقة للبحث.",
  "team.roles.allPermissionsTitle": "صلاحيات {{role}}",
  "team.roles.noPermissions": "هذا الدور بلا صلاحيات.",
  "team.roles.close": "إغلاق",
  "team.roleDialog.addTitle": "إضافة دور جديد",
  "team.roleDialog.editTitle": "تعديل الدور: {{role}}",
  "team.roleDialog.name": "اسم الدور",
  "team.roleDialog.description": "الوصف",
  "team.roleDialog.permissions": "الصلاحيات",
  "team.roleDialog.save": "حفظ الدور",
  "team.roleDialog.cancel": "إلغاء",
  "team.roleDialog.comingSoon": "حفظ الأدوار سيتوفر في المرحلة القادمة.",
  "team.roleDialog.loadError": "تعذّر تحميل قائمة الصلاحيات.",
  "team.members.section": "فريق العمل",
  "team.members.permissionsUnavailable": "تعذّر تحميل الصلاحيات.",
  "team.members.noPermissions": "لا توجد صلاحيات مسندة.",
  "team.members.joined": "انضم في {{date}}",
  "team.members.actionsFor": "إجراءات {{name}}",
  "team.invitations.expires": "تنتهي في {{date}}",
  "team.error.noCompany": "يُرجى اختيار شركة أولًا.",
  "team.error.unauthorized": "انتهت صلاحية جلستك. يُرجى تسجيل الدخول مرة أخرى.",
  "team.error.generic": "حدث خطأ ما. يُرجى المحاولة مرة أخرى.",
  "team.status.active": "نشط",
  "team.status.pending": "معلّقة",
  "team.status.accepted": "مقبولة",
  "team.status.revoked": "مُلغاة",
  "team.status.expired": "منتهية الصلاحية",
  "team.role.custom": "دور مخصص",
  "team.members.title": "أعضاء الفريق",
  "team.members.empty": "لا يوجد أعضاء فريق بعد.",
  "team.members.column.name": "الاسم",
  "team.members.column.email": "البريد الإلكتروني",
  "team.members.column.role": "الدور",
  "team.members.column.status": "الحالة",
  "team.members.column.joinedAt": "تاريخ الانضمام",
  "team.members.actions.remove": "إزالة",
  "team.members.remove.confirmTitle": "إزالة عضو الفريق؟",
  "team.members.remove.confirmDescription":
    "سيفقد {{name}} الوصول إلى هذه الشركة فورًا. لا يمكن التراجع عن هذا الإجراء.",
  "team.members.remove.confirm": "إزالة العضو",
  "team.members.remove.cancel": "إلغاء",
  "team.members.remove.success": "تمت إزالة العضو.",
  "team.invitations.title": "الدعوات المعلّقة",
  "team.invitations.empty": "لا توجد دعوات بعد.",
  "team.invitations.column.role": "الدور",
  "team.invitations.column.status": "الحالة",
  "team.invitations.column.expiresAt": "تنتهي في",
  "team.invitations.actions.revoke": "إلغاء الدعوة",
  "team.invitations.revoke.confirmTitle": "إلغاء الدعوة؟",
  "team.invitations.revoke.confirmDescription":
    "سيتوقف كود دعوة «{{role}}» عن العمل فورًا، ولن يستطيع أي شخص لا يزال يحتفظ به الانضمام.",
  "team.invitations.revoke.confirm": "إلغاء الدعوة",
  "team.invitations.revoke.cancel": "تراجع",
  "team.invitations.revoke.success": "تم إلغاء الدعوة.",
  "team.invite.button": "دعوة عضو",
  "team.invite.title": "دعوة عضو جديد للفريق",
  "team.invite.field.role": "نوع الدور",
  "team.invite.field.predefinedRole": "الدور",
  "team.invite.roleType.predefined": "دور جاهز",
  "team.invite.roleType.custom": "دور مخصص",
  "team.invite.role.owner": "مالك",
  "team.invite.role.manager": "مدير",
  "team.invite.role.store_manager": "مدير متجر",
  "team.invite.role.call_center": "خدمة العملاء",
  "team.invite.role.warehouse": "مسؤول مخزن",
  "team.invite.role.finance": "مالية",
  "team.invite.role.marketing": "تسويق",
  "team.role.vendor": "تاجر",
  "team.role.owner.desc": "صلاحية كاملة على كل شيء في الشركة.",
  "team.role.manager.desc":
    "صلاحيات تشغيل كاملة في الشركة مثل المالك، لكن دون إدارة أدوار الفريق وصلاحياته إلا إذا مُنحت له بشكل منفصل.",
  "team.role.store_manager.desc": "يدير العمليات اليومية للمتجر.",
  "team.role.call_center.desc": "يتابع الطلبات والتواصل مع العملاء.",
  "team.role.warehouse.desc": "يدير المخزون وتجهيز الطلبات والشحن.",
  "team.role.finance.desc": "يتولى المالية والفواتير والتسويات.",
  "team.role.marketing.desc": "التحليلات والتفاعل مع العملاء.",
  "team.role.vendor.desc": "يدير مخزنه فقط، ويُقيَّد تلقائياً بالمخزن الذي انضم إليه.",
  "team.permission.orders.vendorGroupsOverride.name": "تجاوز حالة التاجر",
  "team.permission.orders.vendorGroupsOverride.desc":
    "تغيير حالة أي مجموعة تاجر، بما في ذلك إرجاعها إلى حالة سابقة.",
  "team.permission.messaging.send.name": "الرد في المحادثات",
  "team.permission.messaging.send.desc": "إرسال رسائل داخل محادثة يكون العضو طرفًا فيها.",
  "team.invite.manager.grantAccessManage": "منح هذا المدير صلاحية إدارة أعضاء الفريق والصلاحيات",
  "team.invite.custom.description":
    "مجموعة الصلاحيات هذه خاصة بهذه الدعوة فقط — بعد القبول، تُصبح صلاحيات هذا العضو تحديدًا. هي ليست دورًا قابلًا لإعادة الاستخدام؛ دعوة شخص آخر تعني اختيار الصلاحيات من جديد.",
  "team.invite.custom.loadError": "تعذّر تحميل الصلاحيات المتاحة. يُرجى المحاولة مرة أخرى.",
  "team.invite.custom.empty": "لا توجد صلاحيات متاحة لخطة اشتراك شركتك الحالية.",
  "team.invite.custom.selected": "تم اختيار {{count}} صلاحية",
  "team.invite.custom.selectAll": "اختيار الكل",
  "team.invite.custom.clearAll": "إلغاء الكل",
  "team.invite.back": "رجوع",
  "team.invite.next": "التالي",
  "team.invite.cancel": "إلغاء",
  "team.invite.submit": "إرسال الدعوة",
  "team.invite.submitting": "جارٍ الإرسال…",
  "team.module.core": "عام",
  "team.module.masterData": "البيانات",
  "team.module.products": "المنتجات",
  "team.module.inventory": "المخزون",
  "team.module.customers": "العملاء",
  "team.module.orders": "الطلبات",
  "team.module.shipping": "الشحن",
  "team.module.finance": "المالية",
  "team.module.analytics": "التحليلات",
  "team.module.notifications": "الإشعارات",
  "team.module.messaging": "المحادثات",
  "team.module.storefrontIntegration": "الربط",
  "team.permission.action.read": "عرض",
  "team.permission.action.manage": "إدارة",
  "team.permission.desc.read": "الاطّلاع على {{module}} دون إجراء أي تعديل.",
  "team.permission.desc.manage": "الإضافة والتعديل والحذف في {{module}}.",
  "team.permission.access.read.name": "عرض الصلاحيات",
  "team.permission.access.read.desc": "الاطّلاع على فريق العمل وأدواره وقوالب الصلاحيات.",
  "team.permission.access.manage.name": "إدارة الصلاحيات",
  "team.permission.access.manage.desc": "دعوة الأعضاء وإزالتهم وتغيير صلاحياتهم.",
  "team.permission.orders.assign.name": "إسناد الطلبات",
  "team.permission.orders.assign.desc":
    "إسناد الطلبات إلى أعضاء الفريق، ورؤية كل طلبات الشركة بدل الطلبات المُسنَدة إليه فقط.",
  "team.permission.integrations.manage.name": "إدارة ربط المتجر",
  "team.permission.integrations.manage.desc":
    "إنشاء مفاتيح ربط المتجر الإلكتروني وتدويرها وإلغاؤها.",
  "team.invite.custom.unavailable": "غير متاح في خطتك",
  "team.invite.custom.unavailableHint":
    "الصلاحيات الباهتة تحتاج خاصية غير مشمولة في خطة اشتراكك الحالية.",
  "team.invite.custom.summary": "{{permissions}} صلاحية في {{modules}} قسم",
  "team.invite.custom.moduleCount": "{{selected}}/{{total}}",
  "team.invite.custom.moduleSelectAll": "اختيار كل صلاحيات القسم",
  "team.result.title": "تم إنشاء الدعوة",
  "team.result.description":
    "يظهر هذا الكود مرة واحدة فقط. انسخه الآن وأرسله للشخص الذي تدعوه — يسجّل ببريده الإلكتروني الخاص ثم يُدخل هذا الكود للانضمام. لن تتمكن من رؤيته مرة أخرى.",
  "team.result.code.label": "كود الدعوة",
  "team.result.copy": "نسخ",
  "team.result.copied": "تم النسخ!",
  "team.result.copyFailed": "تعذّر النسخ تلقائيًا — يُرجى تحديد الكود ونسخه يدويًا.",
  "team.result.done": "تم",
  "admin.title": "إدارة المنصّة",
  "admin.subtitle": "إدارة الشركات وخططها والوصول إلى الميزات عبر المنصّة.",
  "admin.companies": "الشركات",
  "admin.plan": "الخطة",
  "admin.noPlan": "بدون خطة",
  "admin.setPlan": "تعيين الخطة",
  "admin.features": "الميزات",
  "admin.loadMore": "تحميل المزيد",
  "admin.empty": "لا توجد شركات بعد.",
  "admin.enable": "تفعيل",
  "admin.disable": "تعطيل",
  "admin.saved": "تم الحفظ",
  "admin.saveFailed": "تعذّر الحفظ. حاول مرة أخرى.",
  "admin.forbidden": "تحتاج إلى صلاحية مسؤول المنصّة لعرض هذه الصفحة.",
  "nav.masterData": "البيانات الأساسية",
  "md.title": "البيانات الأساسية",
  "md.subtitle": "بيانات مرجعية مشتركة تبني عليها كل الوحدات.",
  "md.resources": "الموارد",
  "md.readOnly": "هذه البيانات المرجعية تُدار من النظام وللقراءة فقط.",
  "md.forbidden": "ليس لديك صلاحية الوصول إلى البيانات الأساسية.",
  "md.empty": "لا يوجد شيء هنا بعد.",
  "md.loadMore": "تحميل المزيد",
  "md.saved": "تم الحفظ",
  "md.saveFailed": "تعذّر الحفظ. حاول مرة أخرى.",
  "md.status.title": "الحالة",
  "md.status.active": "نشط",
  "md.status.inactive": "غير نشط",
  "md.actions.create": "جديد",
  "md.actions.edit": "تعديل",
  "md.actions.save": "حفظ",
  "md.actions.cancel": "إلغاء",
  "md.actions.deactivate": "تعطيل",
  "md.resource.units": "الوحدات",
  "md.resource.productCategories": "فئات المنتجات",
  "md.resource.orderLabels": "تصنيفات الطلبات",
  "md.resource.orderReasons": "أسباب الطلبات",
  "md.resource.shippingZones": "مناطق الشحن",
  "md.resource.currencies": "العملات",
  "md.resource.countryConfigs": "الدول",
  "md.resource.governorates": "المحافظات",
  "md.field.name": "الاسم",
  "md.field.code": "الرمز",
  "md.field.color": "اللون",
  "md.field.kind": "النوع",
  "md.field.countryCode": "رمز الدولة",
  "md.field.symbol": "الرمز",
  "md.field.decimalDigits": "المنازل العشرية",
  "md.field.defaultCurrencyCode": "العملة الافتراضية",
  "md.field.phoneCode": "رمز الهاتف",
  "md.kind.cancellation": "إلغاء",
  "md.kind.return": "إرجاع",
  "md.kind.general": "عام",
  "settings.subtitle": "إدارة إعدادات التطبيق واستيراد البيانات.",
  "settings.tab.general": "عام",
  "settings.tab.dataImport": "استيراد البيانات",
  "settings.tab.security": "الأمان",
  "settings.tab.whatsapp": "واتساب",
  "settings.tab.notifications": "الإشعارات",
  "settings.tab.shipping": "الشحن",
  "settings.tab.storefront": "المتجر الإلكتروني",
  "settings.tab.reasons": "الأسباب",
  "settings.reasons.cancellationTitle": "أسباب الإلغاء",
  "settings.reasons.returnTitle": "أسباب الإرجاع",
  "settings.reasons.addPlaceholder": "أدخل سببًا...",
  "settings.reasons.add": "إضافة",
  "settings.reasons.remove": "حذف",
  "settings.reasons.empty": "لم تُضف أسباب بعد.",
  "settings.shipping.zonesTitle": "مناطق الشحن",
  "settings.shipping.zonesEmpty": "لا توجد مناطق شحن. أضف منطقة لتحديد أسعار التوصيل.",
  "settings.shipping.addZone": "إضافة منطقة",
  "settings.shipping.carriersTitle": "شركات الشحن",
  "settings.shipping.carriersSubtitle": "شركات الشحن المتاحة لإنشاء الشحنات وبوالص الشحن.",
  "settings.shipping.available": "متاح",
  "settings.shipping.connected": "متصل",
  "settings.shipping.notConnected": "غير متصل",
  "settings.shipping.connectAction": "ربط",
  "settings.shipping.disconnectAction": "فصل",
  "settings.shipping.apiKeyPlaceholder": "مفتاح API",
  "settings.shipping.pickupLocationWarning":
    "متصل، لكن لا يوجد موقع استلام مُعدّ في حساب بوسطة بعد.",
  "settings.shipping.connectInvalidKey": "تم رفض مفتاح API هذا. تحقق منه وحاول مرة أخرى.",
  "settings.shipping.connectUnavailable": "تعذّر الوصول إلى شركة الشحن. حاول مرة أخرى بعد قليل.",
  "settings.shipping.connectFailed": "تعذّر الربط. حاول مرة أخرى.",
  "storefront.forbidden": "ليس لديك صلاحية الوصول إلى تكامل المتجر الإلكتروني.",
  "storefront.title": "اتصالات المتجر الإلكتروني",
  "storefront.subtitle":
    "اربط متجرك الإلكتروني (أو أكثر) — كل اتصال يحصل على مفتاح API خاص به. الطلبات والمنتجات التي يرسلها متجرك تظهر تلقائيًا.",
  "storefront.empty": "لا توجد اتصالات متجر إلكتروني حتى الآن.",
  "storefront.loadMore": "تحميل المزيد",
  "storefront.saved": "تم الحفظ.",
  "storefront.saveFailed": "تعذّر الحفظ. حاول مرة أخرى.",
  "storefront.revoked": "تم إلغاء الاتصال.",
  "storefront.rotateFailed": "تعذّر تدوير المفتاح. حاول مرة أخرى.",
  "storefront.revokeFailed": "تعذّر إلغاء الاتصال. حاول مرة أخرى.",
  "storefront.actions.create": "اتصال جديد",
  "storefront.actions.edit": "تعديل",
  "storefront.actions.rotate": "تدوير المفتاح",
  "storefront.actions.revoke": "إلغاء",
  "storefront.actions.save": "حفظ",
  "storefront.actions.cancel": "إلغاء",
  "storefront.actions.close": "إغلاق",
  "storefront.actions.viewEvents": "عرض الأحداث",
  "storefront.field.label": "الاسم",
  "storefront.field.platform": "المنصّة",
  "storefront.field.apiKeyPrefix": "مفتاح API",
  "storefront.field.defaultWarehouse": "المستودع الافتراضي",
  "storefront.field.noWarehouse": "بلا مستودع افتراضي",
  "storefront.field.status": "الحالة",
  "storefront.field.active": "نشط (غير محدد = متوقف مؤقتًا)",
  "storefront.field.lastEventAt": "آخر حدث",
  "storefront.field.webhookSecret": "سر Webhook",
  "storefront.field.webhookSecretHint":
    "من إعدادات الـ webhook الخاصة بـ WooCommerce. اختياري — يُتحقق منه إضافةً لمفتاح API. لا يُعرض مرة أخرى بعد الحفظ.",
  "storefront.platform.generic": "عام (متجر مخصّص)",
  "storefront.platform.salla": "سلة",
  "storefront.platform.zid": "زد",
  "storefront.platform.shopify": "Shopify",
  "storefront.platform.woocommerce": "WooCommerce",
  "storefront.status.active": "نشط",
  "storefront.status.paused": "متوقف مؤقتًا",
  "storefront.status.revoked": "ملغى",
  "storefront.create.title": "اتصال متجر إلكتروني جديد",
  "storefront.create.labelTaken": "يوجد اتصال بهذا الاسم بالفعل.",
  "storefront.edit.title": "تعديل الاتصال",
  "storefront.rotate.confirmTitle": "تدوير مفتاح API لهذا الاتصال؟",
  "storefront.rotate.confirmDescription":
    "المفتاح الحالي يتوقف عن العمل فورًا. يجب تحديث متجرك بالمفتاح الجديد — سيظهر مرة واحدة فقط بعد هذا مباشرة.",
  "storefront.revoke.confirmTitle": "إلغاء هذا الاتصال؟",
  "storefront.revoke.confirmDescription":
    "هذا إجراء نهائي — مفتاح الاتصال يتوقف عن العمل فورًا ولا يمكن إعادة تفعيله. أنشئ اتصالًا جديدًا لإعادة ربط هذا المتجر.",
  "storefront.revoke.confirmAction": "نعم، إلغاء الاتصال",
  "storefront.apiKey.title": "مفتاح API — يظهر مرة واحدة",
  "storefront.apiKey.warning":
    "انسخ هذا المفتاح الآن واحفظه بأمان. لأسباب أمنية، لن يظهر مرة أخرى — ستحتاج لتدوير المفتاح للحصول على واحد جديد.",
  "storefront.apiKey.copy": "نسخ إلى الحافظة",
  "storefront.apiKey.copied": "تم النسخ إلى الحافظة.",
  "storefront.apiKey.copyFailed": "تعذّر النسخ. حدد المفتاح وانسخه يدويًا.",
  "storefront.apiKey.ack": "لقد نسخت هذا المفتاح",
  "storefront.events.title": "الأحداث الأخيرة",
  "storefront.events.empty": "لا توجد أحداث بعد لهذا الاتصال.",
  "storefront.events.filter.status": "تصفية حسب الحالة",
  "storefront.events.filter.allStatuses": "كل الحالات",
  "storefront.events.filter.eventType": "تصفية حسب النوع",
  "storefront.events.filter.allTypes": "كل الأنواع",
  "storefront.events.field.attempts": "عدد المحاولات",
  "storefront.events.status.pending": "قيد الانتظار",
  "storefront.events.status.processed": "تمت المعالجة",
  "storefront.events.status.failed": "فشل",
  "storefront.events.type.order": "طلب",
  "storefront.events.type.product": "منتج",
  "storefront.events.reprocess": "إعادة المعالجة",
  "storefront.events.reprocessSuccess": "تمت إعادة معالجة الحدث.",
  "storefront.events.reprocessFailed": "تعذّرت إعادة معالجة الحدث. حاول مرة أخرى.",
  "storefront.vendorWarehouses.title": "مستودعات التجار",
  "storefront.vendorWarehouses.description":
    "وجّه طلبات كل تاجر في المتجر لمستودعه الخاص. أي تاجر بدون ربط هنا يفشل الطلب بدلًا من تخمين مستودع.",
  "storefront.vendorWarehouses.empty": "لا يوجد تجار مربوطين حتى الآن.",
  "storefront.vendorWarehouses.field.vendorId": "معرّف التاجر",
  "storefront.vendorWarehouses.field.vendorIdHint":
    "معرّف التاجر كما تبعثه المنصة (WooCommerce/WCFM: رقم مستخدم WordPress الخاص بالتاجر).",
  "storefront.vendorWarehouses.field.warehouse": "المستودع",
  "storefront.vendorWarehouses.actions.add": "إضافة ربط",
  "storefront.vendorWarehouses.actions.remove": "إزالة",
  "storefront.vendorWarehouses.remove.confirmTitle": "إزالة ربط هذا التاجر؟",
  "storefront.vendorWarehouses.remove.confirmDescription":
    "طلبات هذا التاجر هتفشل تاني لحد ما يترّبط بمستودع من جديد.",
  "storefront.vendorWarehouses.remove.confirmAction": "نعم، إزالة الربط",
  "storefront.vendorWarehouses.removed": "تمت إزالة الربط.",
  "storefront.vendorWarehouses.removeFailed": "تعذّرت إزالة الربط. حاول مرة أخرى.",
  "storefront.vendorWarehouses.addFailed": "تعذّرت إضافة الربط. حاول مرة أخرى.",
  "storefront.vendorWarehouses.duplicateVendor": "هذا التاجر مربوط بالفعل على هذا الاتصال.",
  "settings.general.languageTitle": "اللغة",
  "settings.general.languageSubtitle": "اختر لغتك المفضلة.",
  "settings.general.arabic": "العربية",
  "settings.general.english": "English",
  "settings.general.subscriptionTitle": "الاشتراك",
  "settings.general.subscriptionPending": "تفاصيل الاشتراك والفوترة غير متاحة بعد.",
  "settings.security.changePasswordTitle": "تغيير كلمة المرور",
  "settings.security.currentPassword": "كلمة المرور الحالية",
  "settings.security.newPassword": "كلمة المرور الجديدة",
  "settings.security.confirmPassword": "تأكيد كلمة المرور",
  "settings.security.updatePassword": "تحديث كلمة المرور",
  "settings.security.mismatch": "كلمة المرور الجديدة وتأكيدها غير متطابقين.",
  "settings.security.changeSuccess": "تم تحديث كلمة المرور.",
  "settings.security.changeFailed": "تعذّر تحديث كلمة المرور. حاول مرة أخرى.",
  "settings.security.wrongPassword": "كلمة المرور الحالية غير صحيحة.",
  "settings.security.deleteTitle": "حذف الحساب",
  "settings.security.deleteSubtitle": "طلب حذف حسابك نهائيًا.",
  "settings.security.deleteAction": "طلب حذف الحساب",
  "settings.security.deleteConfirm": "هل أنت متأكد؟ لا يمكن التراجع عن هذا الإجراء.",
  "settings.security.deleteConfirmYes": "نعم، اطلب الحذف",
  "settings.security.deleteRequested": "تم إرسال طلب الحذف للمراجعة.",
  "settings.security.deleteFailed": "تعذّر إرسال الطلب. حاول مرة أخرى.",
  "settings.dataImport.productsTitle": "استيراد المنتجات",
  "settings.dataImport.productsDescription":
    "استيراد المنتجات بالجملة من ملف CSV مُصدَّر من Excel أو Sheets. اربط أعمدة الاسم والرمز وبيانات المخزون.",
  "settings.dataImport.ordersTitle": "استيراد الطلبات",
  "settings.dataImport.ordersDescription":
    "استيراد بيانات الطلبات السابقة من ملف CSV أو Excel. اربط الأعمدة لمطابقة بياناتك الحالية.",
  "settings.dataImport.csvHint": 'ملف CSV (.csv). صدّر الملف من Excel أو Sheets بصيغة "CSV" أولاً.',
  "settings.dataImport.notMapped": "— غير مربوط —",
  "settings.dataImport.startImport": "بدء الاستيراد",
  "settings.dataImport.importing": "جارٍ الاستيراد…",
  "settings.dataImport.importAnother": "استيراد ملف آخر",
  "settings.dataImport.failed": "تعذّر بدء عملية الاستيراد. حاول مرة أخرى.",
  "settings.dataImport.summary": "تم استيراد {{succeeded}}، وفشل {{failed}}.",
  "settings.dataImport.rowError": "الصف {{row}}: {{message}}",
  "settings.dataImport.description": "عمود الوصف",
  "settings.dataImport.categoryId": "عمود معرّف الفئة",
  "settings.dataImport.unitId": "عمود معرّف الوحدة",
  "settings.dataImport.sku": "عمود الرمز (SKU)",
  "settings.dataImport.barcode": "عمود الباركود",
  "settings.dataImport.customerId": "عمود معرّف العميل",
  "settings.dataImport.variantId": "عمود معرّف الصنف",
  "settings.dataImport.quantity": "عمود الكمية",
  "settings.dataImport.price": "عمود السعر (بوحدات فرعية)",
  "settings.dataImport.shippingFee": "عمود رسوم الشحن",
  "settings.dataImport.discount": "عمود الخصم",
  "settings.whatsapp.countryCodeTitle": "بادئة الدولة",
  "settings.whatsapp.countryCodeSubtitle": "رمز الدولة يُضاف قبل أرقام الهاتف (مثال: 20 لمصر).",
  "settings.whatsapp.templatesTitle": "قوالب الرسائل",
  "settings.whatsapp.templatesSubtitle": "قوالب رسائل قابلة لإعادة الاستخدام لمحادثات واتساب.",
  "settings.whatsapp.templateBody": "نص القالب",
  "settings.whatsapp.addTemplate": "إضافة قالب",
  "settings.whatsapp.templatesEmpty": "لا توجد قوالب بعد.",
  "customers.title": "العملاء",
  "customers.subtitle": "الأشخاص الذين يشترون منك، وبيانات تواصلهم وعناوينهم.",
  "customers.forbidden": "ليس لديك صلاحية الوصول إلى العملاء.",
  "customers.empty": "لا يوجد عملاء بعد.",
  "customers.loadMore": "تحميل المزيد",
  "customers.saved": "تم الحفظ",
  "customers.saveFailed": "تعذّر الحفظ. يُرجى المحاولة مرة أخرى.",
  "customers.duplicatePhone": "يوجد عميل بهذا الرقم بالفعل.",
  "customers.invalidPhone": "أدخل الرقم بالصيغة الدولية، مثل ‎+201001234567.",
  "customers.search.label": "بحث",
  "customers.search.placeholder": "رقم هاتف كامل، أو اسم أو بريد إلكتروني…",
  "customers.search.submit": "بحث",
  "customers.search.hint": "يجب أن يكون رقم الهاتف كاملًا — الرقم الجزئي لا يطابق شيئًا.",
  "customers.status.title": "الحالة",
  "customers.status.active": "نشط",
  "customers.status.inactive": "مؤرشف",
  "customers.actions.create": "عميل جديد",
  "customers.actions.details": "التفاصيل",
  "customers.actions.edit": "تعديل",
  "customers.actions.save": "حفظ",
  "customers.actions.cancel": "إلغاء",
  "customers.actions.archive": "أرشفة",
  "customers.field.name": "الاسم",
  "customers.field.phone": "الهاتف",
  "customers.field.phoneFull": "الهاتف (كاملًا)",
  "customers.field.phoneHint": "بالصيغة الدولية، مثل ‎+201001234567.",
  "customers.field.phoneEditHint": "اتركه فارغًا للإبقاء على الرقم الحالي.",
  "customers.field.email": "البريد الإلكتروني",
  "customers.field.notes": "ملاحظات",
  "customers.form.sectionBasic": "البيانات الأساسية",
  "customers.form.sectionAddress": "العنوان",
  "customers.kpi.orders": "الطلبات",
  "customers.kpi.spent": "إجمالي الإنفاق",
  "customers.kpi.lastOrder": "آخر طلب",
  "customers.detail.title": "تفاصيل العميل",
  "customers.purchaseHistory.title": "سجل الشراء",
  "customers.purchaseHistory.empty": "لا يوجد طلبات بعد.",
  "customers.purchaseHistory.noReview": "بدون تقييم",
  "customers.addresses.title": "العناوين",
  "customers.addresses.add": "إضافة عنوان",
  "customers.addresses.edit": "تعديل العنوان",
  "customers.addresses.empty": "لا توجد عناوين بعد.",
  "customers.addresses.default": "افتراضي",
  "customers.address.field.line": "العنوان",
  "customers.address.field.governorate": "المحافظة",
  "customers.address.field.area": "المنطقة/الحي",
  "customers.address.field.landmark": "علامة مميزة",
  "customers.address.field.bostaCity": "المحافظة",
  "customers.address.field.bostaDistrict": "الحي",
  "customers.address.field.isDefault": "العنوان الافتراضي",
  "customers.address.source.storefront": "متزامن من الموقع",
  "products.title": "المنتجات",
  "products.subtitle": "كتالوج منتجاتك ومتغيّراتها القابلة للبيع.",
  "products.forbidden": "ليس لديك صلاحية الوصول إلى المنتجات.",
  "products.empty": "لا توجد منتجات بعد.",
  "products.loadMore": "تحميل المزيد",
  "products.saved": "تم الحفظ",
  "products.saveFailed": "تعذّر الحفظ. يُرجى المحاولة مرة أخرى.",
  "products.search.label": "بحث",
  "products.search.placeholder": "ابحث بالاسم أو SKU أو الباركود…",
  "products.search.submit": "بحث",
  "products.status.title": "الحالة",
  "products.status.active": "نشط",
  "products.status.inactive": "مؤرشف",
  "products.actions.create": "منتج جديد",
  "products.actions.edit": "تعديل",
  "products.actions.save": "حفظ",
  "products.actions.cancel": "إلغاء",
  "products.actions.archive": "أرشفة",
  "products.field.name": "الاسم",
  "products.field.description": "الوصف",
  "products.field.category": "الفئة",
  "products.field.unit": "الوحدة",
  "products.field.image": "الصورة",
  "products.field.imageHint": "رابط صورة مباشر (مثلًا من متجرك الإلكتروني).",
  "products.field.warehouse": "المستودع",
  "products.field.warehouseNone": "بدون",
  "products.field.allowOversell": "السماح بالبيع بما يتجاوز المتاح",
  "products.form.sectionBasic": "البيانات الأساسية",
  "products.form.sectionVariant": "الخيار",
  "products.variants.title": "المتغيّرات",
  "products.variants.toggle": "المتغيّرات",
  "products.variants.add": "إضافة متغيّر",
  "products.variants.empty": "لا توجد متغيّرات بعد.",
  "products.variant.field.name": "الاسم",
  "products.variant.field.sku": "SKU",
  "products.variant.field.barcode": "الباركود",
  "products.variant.field.warehouse": "المستودع",
  "products.variant.warehouseSaved": "تم تحديث المستودع",
  "products.variant.warehouseFailed": "تعذّر تحديث المستودع. يُرجى المحاولة مرة أخرى.",
  "products.filter.warehouse": "المستودع",
  "products.filter.clear": "مسح الفلاتر",
  "inventory.title": "المخزون",
  "inventory.subtitle": "مستويات المخزون في مواقعك، والمواقع نفسها.",
  "inventory.forbidden": "ليس لديك صلاحية الوصول إلى المخزون.",
  "inventory.saved": "تم الحفظ",
  "inventory.saveFailed": "تعذّر الحفظ. يُرجى المحاولة مرة أخرى.",
  "inventory.loadMore": "تحميل المزيد",
  "inventory.tab.stock": "المخزون",
  "inventory.tab.warehouses": "المستودعات",
  "inventory.filter.warehouse": "المستودع",
  "inventory.filter.lowOnly": "المنخفض فقط",
  "inventory.filter.clear": "مسح الفلاتر",
  "inventory.stock.empty": "لا توجد مستويات مخزون بعد.",
  "inventory.stock.low": "منخفض",
  "inventory.stock.onHand": "المتوفر فعليًا",
  "inventory.stock.committed": "المحجوز",
  "inventory.stock.available": "القابل للبيع",
  "inventory.stock.reorderPoint": "حد إعادة الطلب",
  "inventory.warehouses.empty": "لا توجد مستودعات بعد.",
  "inventory.status.title": "الحالة",
  "inventory.status.active": "نشط",
  "inventory.status.inactive": "مؤرشف",
  "inventory.actions.create": "مستودع جديد",
  "inventory.actions.edit": "تعديل",
  "inventory.actions.joinCode": "كود الدعوة",
  "inventory.actions.archive": "أرشفة",
  "inventory.actions.save": "حفظ",
  "inventory.actions.cancel": "إلغاء",
  "inventory.actions.adjust": "تسوية المخزون",
  "inventory.actions.transfer": "تحويل المخزون",
  "inventory.actions.setReorderPoint": "تعيين حد إعادة الطلب",
  "inventory.field.name": "الاسم",
  "inventory.field.code": "الرمز",
  "inventory.field.address": "العنوان",
  "inventory.field.isDefault": "الموقع الافتراضي",
  "inventory.field.warehouse": "المستودع",
  "inventory.field.fromWarehouse": "من مستودع",
  "inventory.field.toWarehouse": "إلى مستودع",
  "inventory.field.variant": "المتغيّر",
  "inventory.field.image": "الصورة",
  "inventory.field.product": "المنتج",
  "inventory.variantPicker.selectWarehouse": "اختر المستودع أولًا",
  "inventory.variantPicker.loading": "جارٍ تحميل المتغيّرات…",
  "inventory.variantPicker.empty": "لا توجد متغيّرات في هذا المستودع بعد",
  "inventory.variantPicker.error": "تعذّر تحميل متغيّرات هذا المستودع",
  "inventory.field.quantity": "الكمية",
  "inventory.field.quantityDelta": "التغيير (+/−)",
  "inventory.field.reason": "السبب",
  "inventory.field.note": "ملاحظة",
  "inventory.reason.count": "جرد",
  "inventory.reason.damage": "تلف",
  "inventory.reason.loss": "فقد",
  "inventory.reason.return": "مرتجع",
  "inventory.reason.other": "أخرى",
  "inventory.joinCode.title": "كود الدعوة — {{name}}",
  "inventory.joinCode.close": "إغلاق",
  "inventory.joinCode.loadError": "تعذّر تحميل كود الدعوة. حاول مرة أخرى.",
  "inventory.joinCode.shownOnce":
    "يظهر هذا الكود مرة واحدة فقط. انسخه الآن وشاركه مع التاجر — لن تتمكن من رؤيته مرة أخرى.",
  "inventory.joinCode.codeLabel": "كود دعوة المستودع",
  "inventory.joinCode.statusActive": "نشط — شاركه مع التاجر للانضمام إلى هذا المستودع.",
  "inventory.joinCode.statusRevoked": "تم إلغاؤه. أنشئ كودًا جديدًا للسماح لتاجر بالانضمام.",
  "inventory.joinCode.statusNone": "لا يوجد كود دعوة بعد لهذا المستودع.",
  "inventory.joinCode.rotate": "تدوير (إصدار كود جديد)",
  "inventory.joinCode.create": "إنشاء كود دعوة",
  "inventory.joinCode.revoke": "إلغاء",

  "orders.title": "الطلبات",
  "orders.subtitle": "دورة حياة الطلب من الاستقبال حتى الإغلاق — الحالة والإسناد والتحصيل.",
  "orders.forbidden": "لا تملك صلاحية الوصول إلى الطلبات.",
  "orders.empty": "لا توجد طلبات هنا.",
  "orders.saved": "تم الحفظ",
  "orders.saveFailed": "تعذّر حفظ الطلب.",
  "orders.invalid": "هذا التغيير غير مسموح لهذا الطلب.",
  "orders.stockShortageLine": "المطلوب {{requested}}، المتاح {{available}}",
  "orders.reasonRequired": "الإلغاء يتطلب سببًا — افتح الطلب لاختياره.",
  "orders.cancelModal.title": "إلغاء الطلب",
  "orders.cancelModal.reasonLabel": "سبب الإلغاء",
  "orders.cancelModal.reasonPlaceholder": "اختر سببًا",
  "orders.cancelModal.cancel": "رجوع",
  "orders.cancelModal.confirm": "إلغاء الطلب",
  "orders.loadMore": "تحميل المزيد",
  "orders.board.label": "الطلبات حسب الحالة",
  "orders.board.dropHere": "أفلت هنا لنقل الطلب إلى هذه الحالة",
  "orders.board.empty": "لا توجد طلبات",
  "orders.board.dragHint": "اسحب الطلب إلى حالة أخرى لنقله.",
  "orders.board.moved": "الطلب رقم ⁧{{order}}⁩ انتقل إلى {{status}}.",
  "orders.board.moveFailed": "تعذّر نقل الطلب.",
  "orders.board.changeStatus": "تغيير الحالة",
  "orders.board.changeStatusFor": "تغيير حالة الطلب رقم ⁧{{order}}⁩",
  "orders.tabs.all": "الكل",
  "orders.search.label": "بحث",
  "orders.search.placeholder": "رقم الطلب أو اسم العميل",
  "orders.search.submit": "بحث",
  "orders.status.new": "جديد",
  "orders.status.confirming": "جاري التأكيد",
  "orders.status.processing": "قيد المعالجة",
  "orders.status.incomplete": "ناقص",
  "orders.status.ready": "جاهز",
  "orders.status.shipped": "تم الشحن",
  "orders.status.delivered": "تم التسليم",
  "orders.status.completed": "مكتمل",
  "orders.status.postponed": "مؤجل",
  "orders.status.cancelled": "ملغي",
  "orders.status.returned": "مرتجع",
  "orders.status.exchanged": "مستبدل",
  "orders.payment.unpaid": "غير مدفوع",
  "orders.payment.partial": "مدفوع جزئيًا",
  "orders.payment.paid": "مدفوع",
  "orders.field.items": "الأصناف",
  "orders.item.notInCrm": "غير موجود في الـ CRM",
  "orders.item.notInCrmHint":
    "المتجر باع منتجًا لم يصل إلى الـ CRM بعد. ستكتمل بياناته تلقائيًا عند مزامنة المنتج.",
  "orders.field.total": "الإجمالي",
  "orders.field.collected": "المُحصّل",
  "orders.field.remaining": "المتبقي",
  "orders.field.payment": "الدفع",
  "orders.field.orderNumber": "رقم الطلب",
  "orders.field.tags": "الوسوم",
  "orders.field.assigned": "مسند إلى",
  "orders.field.createdAt": "تاريخ الإنشاء",
  "orders.status.title": "الحالة",
  "orders.actions.create": "طلب جديد",
  "orders.actions.details": "التفاصيل",
  "orders.actions.changeStatus": "الحالة",
  "orders.actions.save": "حفظ",
  "orders.actions.cancel": "إلغاء",
  "orders.actions.rowMenu": "إجراءات الصف",
  "orders.actions.transitionTo": "نقل إلى",
  "orders.actions.export": "تصدير",
  "orders.actions.print": "طباعة",
  "orders.actions.tags": "الوسوم",
  "orders.actions.advancedFilters": "فلاتر متقدمة",
  "orders.actions.columns": "الأعمدة",
  "orders.actions.more": "المزيد",
  "orders.actions.copyOrderNumber": "نسخ رقم الطلب",
  "orders.actions.copied": "تم النسخ",
  "orders.filters.allPayments": "كل حالات الدفع",
  "orders.filters.dateFrom": "من تاريخ",
  "orders.filters.dateTo": "إلى تاريخ",
  "orders.filters.advanced": "فلاتر متقدمة",
  "orders.filters.count": "فلاتر",
  "orders.filters.reset": "إعادة تعيين",
  "orders.footer.showing": "عرض {{count}} من {{total}}",
  "orders.detail.title": "طلب رقم {{number}}",
  "orders.detail.tabs.summary": "ملخص",
  "orders.detail.tabs.assignee": "الإسناد",
  "orders.detail.assignee.unassigned": "غير مُسند",
  "orders.detail.assignee.change": "إسناد إلى",
  "orders.detail.tabs.customer": "العميل",
  "orders.detail.tabs.items": "الأصناف",
  "orders.detail.tabs.shipping": "الشحن",
  "orders.detail.tabs.payments": "المدفوعات",
  "orders.detail.tabs.activities": "الأنشطة",
  "orders.detail.tabs.timeline": "الجدول الزمني",
  "orders.detail.tabs.notes": "ملاحظات",
  "orders.detail.tabs.vendorTracking": "تتبع التجار",
  "orders.detail.tabs.review": "التقييم",
  "orders.detail.vendorTracking.noVendor": "لم ينضم أي تاجر لهذا المستودع بعد",
  "orders.detail.vendorTracking.updatedAt": "آخر تحديث",
  "orders.detail.vendorTracking.overallStatus": "الحالة العامة",
  "orders.detail.vendorTracking.progress": "{{delivered}} من {{total}} تجار تم التسليم",
  "orders.detail.vendorTracking.itemsCount": "{{count}} منتج",
  "orders.detail.vendorTracking.allDelivered.title": "كل التجار تم التسليم",
  "orders.detail.vendorTracking.allDelivered.description":
    "كل المنتجات أصبحت جاهزة للاستلام من جميع التجار",
  "orders.detail.loadingPhone": "جاري تحميل رقم الهاتف…",
  "orders.detail.delivery.title": "التوصيل",
  "orders.detail.delivery.noAddress": "لا يوجد عنوان توصيل لهذا الطلب.",
  "orders.detail.call": "اتصال",
  "orders.detail.whatsapp": "واتساب",
  "orders.detail.section.financial": "الملخص المالي",
  "orders.detail.section.orderInfo": "معلومات الطلب",
  "orders.whatsapp.rowButtonLabel": "إرسال رسالة واتساب",
  "orders.whatsapp.promptTitle": "تحب تبعت للعميل رسالة واتساب عن الطلب ده؟",
  "orders.whatsapp.send": "إرسال عبر واتساب",
  "orders.whatsapp.invalidPhone": "رقم العميل غير صالح لإرسال رسالة واتساب.",
  "orders.whatsapp.messageConfirming":
    "مرحبًا {{customerName}} 👋\nنود تأكيد استلامنا لطلبكم رقم #{{orderNumber}} من {{companyName}}.\nهل يمكنكم تأكيد الطلب لمتابعة التجهيز؟\nشكرًا لثقتكم بنا 🌸",
  "orders.whatsapp.messageReady":
    "مرحبًا {{customerName}} 👋\nيسعدنا إبلاغكم بأن طلبكم رقم #{{orderNumber}} من {{companyName}} أصبح جاهزًا، وسيتم شحنه قريبًا.\nشكرًا لتعاملكم معنا 🌸",
  "orders.whatsapp.messageShipped":
    "مرحبًا {{customerName}} 👋\nتم شحن طلبكم رقم #{{orderNumber}} من {{companyName}}، وسيصلكم قريبًا بإذن الله.\nنتمنى لكم تجربة ممتعة 🌸",
  "orders.savedViews.all": "كل الطلبات",
  "orders.savedViews.mine": "طلباتي",
  "orders.savedViews.today": "اليوم",
  "orders.savedViews.late": "متأخر",
  "orders.savedViews.cancelled": "ملغي",
  "orders.savedViews.label": "طرق العرض المحفوظة",
  "orders.grid.sortHint": "يرتّب الصفوف المحمّلة فقط",
  "orders.bulk.selected": "{{count}} محدد",
  "orders.bulk.clear": "مسح",
  "orders.mobile.selectedCount": "{{count}} طلب محدّد",
  "orders.mobile.changeStatusTo": "تغيير الحالة إلى",
  "orders.mobile.noSharedStatus": "الطلبات المحدّدة ما فيهاش حالة مشتركة تنتقل ليها.",
  "orders.mobile.selectOrder": "تحديد الطلب رقم {{order}}",
  "orders.bulk.changeStatus": "تغيير الحالة",
  "orders.bulk.assign": "إسناد",
  "orders.form.customer": "العميل",
  "orders.form.variant": "المنتج / المتغيّر",
  "orders.form.quantity": "الكمية",
  "orders.form.price": "السعر",
  "orders.form.addLine": "إضافة سطر",
  "orders.form.addLineHint": "اختر المنتج أولاً.",
  "orders.form.noLines": "لا أسطر بعد.",
  "orders.form.shipping": "الشحن",
  "orders.form.giftWrap": "تغليف الهدية",
  "orders.form.discount": "الخصم",
  "orders.form.warehouse": "المخزن",
  "orders.form.newCustomer": "عميل جديد",
  "orders.form.saveCustomer": "حفظ العميل",
  "orders.form.customerName": "الاسم",
  "orders.form.customerPhone": "رقم الهاتف",
  "orders.form.customerSecondaryPhone": "رقم هاتف إضافي",
  "orders.form.customerGovernorate": "المحافظة",
  "orders.form.customerCity": "المدينة",
  "orders.form.customerStreet": "العنوان التفصيلي",
  "orders.form.addressSaveFailed": "تم حفظ العميل، لكن تعذّر حفظ العنوان.",
  "orders.form.paymentStatus": "حالة الدفع",
  "orders.form.paymentStatus.unpaid": "غير مدفوع",
  "orders.form.paymentStatus.paid": "مدفوع",
  "orders.form.paymentStatus.partial": "مدفوع جزئياً",
  "orders.form.paymentStatus.error": "المبلغ المدفوع لا يطابق حالة الدفع المختارة.",
  "orders.form.paidAmount": "المبلغ المدفوع",
  "orders.form.remainingAmount": "المبلغ المتبقي",
  "orders.form.notes": "ملاحظات",
  "orders.form.salesChannel": "مصدر الطلب",
  "orders.form.salesChannelHint": "الطلب ده جه منين. اضغط تاني عشان تلغي الاختيار.",
  "orders.channel.facebook": "فيسبوك",
  "orders.channel.whatsapp": "واتساب",
  "orders.channel.tiktok": "تيك توك",
  "orders.channel.instagram": "إنستجرام",
  "orders.channel.phone": "مكالمة تليفونية",
  "orders.channel.storefront": "المتجر",
  "orders.form.summary.title": "ملخص الطلب",
  "orders.form.summary.subtotal": "الإجمالي الفرعي",
  "orders.form.summary.discount": "الخصم",
  "orders.form.summary.shipping": "الشحن",
  "orders.form.summary.total": "الإجمالي الكلي",
  "orders.form.header.subtitle": "إضافة طلب جديد للنظام",
  "orders.form.pasteHint": "الصق محادثة العميل لاستخراج رقم الهاتف والمنتجات",
  "orders.form.pastePlaceholder": "اكتب أو الصق رسالة العميل هنا …",
  "orders.form.warehouseHint": "اختر المخزن الخاص بالطلب",
  "orders.form.customerHint": "اختر العميل أو أضف عميل جديد",
  "orders.form.variantHint": "اختر المنتج أو المتغير المطلوب",
  "orders.form.variantSearchFailed": "تعذّر تحميل المنتجات. تأكد من الاتصال وحاول مرة أخرى.",
  "orders.form.currency": "EGP",
  "orders.form.addToCart": "إضافة لسلة الطلب",
  "orders.form.linesTitle": "المنتجات المضافة",
  "orders.form.lineProduct": "المنتج",
  "orders.form.lineTotal": "الإجمالي",
  "orders.form.lineActions": "إجراءات",
  "orders.form.removeLine": "إزالة {{name}}",
  "orders.form.emptyCartTitle": "لا توجد منتجات مضافة بعد",
  "orders.form.emptyCartHint": "اختر منتجاً وأضفه إلى سلة الطلب",
  "orders.form.pricingTitle": "الخصم والشحن",
  "orders.form.pricingHint": "يمكنك إضافة خصم أو تكلفة شحن",
  "orders.form.notesPlaceholder": "أي ملاحظات إضافية …",
  "orders.form.saveOrder": "حفظ الطلب",
  "orders.detail.items": "الأصناف",
  "orders.detail.activity": "النشاط",
  "orders.detail.collect": "مبلغ التحصيل",
  "orders.paste.label": "لصق ذكي (من محادثة)",
  "orders.paste.button": "استخراج",
  "orders.paste.detected": "المُستخرَج",
  "shipping.forbidden": "لا تملك صلاحية الوصول إلى الشحن.",
  "shipping.section.title": "الشحنة",
  "shipping.none": "لا توجد شحنة بعد.",
  "shipping.lastCancelled": "تم إلغاء آخر شحنة — يمكنك إنشاء شحنة جديدة.",
  "shipping.saved": "تم الحفظ",
  "shipping.saveFailed": "تعذّر تحديث الشحنة.",
  "shipping.invalid": "هذا التغيير غير مسموح لهذه الشحنة.",
  "shipping.field.carrier": "شركة الشحن",
  "shipping.field.tracking": "رقم التتبع",
  "shipping.field.status": "الحالة",
  "shipping.field.fee": "رسوم الشحن",
  "shipping.field.deliveredAt": "تاريخ التسليم",
  "shipping.status.created": "تم الإنشاء",
  "shipping.status.picked_up": "تم الاستلام",
  "shipping.status.in_transit": "قيد الشحن",
  "shipping.status.delivered": "تم التسليم",
  "shipping.status.returned": "مرتجعة",
  "shipping.status.cancelled": "ملغاة",
  "shipping.actions.create": "إنشاء شحنة",
  "shipping.actions.advance": "تقديم إلى",
  "shipping.actions.cancel": "إلغاء الشحنة",
  "shipping.actions.waybill": "بوليصة الشحن",
  "shipping.waybillIssued": "تم إصدار البوليصة",
  "shipping.cancelSuccess": "تم إلغاء الشحنة بنجاح.",
  "shipping.confirmCancel.title": "إلغاء الشحنة",
  "shipping.confirmCancel.message":
    "هل أنت متأكد من إلغاء هذه الشحنة؟ لا يمكن التراجع عن هذه العملية.",
  "shipping.confirmCancel.confirm": "تأكيد الإلغاء",
  "shipping.confirmCancel.cancel": "رجوع",
  "review.section.title": "تقييم العميل",
  "review.none": "لا يوجد تقييم بعد.",
  "review.actions.add": "إضافة تقييم",
  "review.actions.save": "حفظ التقييم",
  "review.actions.cancel": "إلغاء",
  "review.saved": "تم حفظ التقييم.",
  "review.saveFailed": "تعذّر حفظ التقييم.",
  "review.field.productType": "نوع المنتجات",
  "review.productType.clothes": "ملابس",
  "review.productType.electronics": "أدوات إلكترونيات",
  "review.productType.gifts": "هدايا",
  "review.field.giftRecipientName": "اسم الشخص المُهدى إليه",
  "review.field.giftRecipientRelation": "الصلة بالعميل",
  "review.field.giftOccasion": "المناسبة",
  "review.field.quality": "الجودة",
  "review.field.packaging": "التغليف",
  "review.field.shipping": "الشحن",
  "review.field.lowReason": "سبب التقييم المنخفض",
  "review.field.average": "المتوسط العام",
  "shipping.orderNotReady":
    "لا يمكن إنشاء شحنة لأن الطلب ما زال في مرحلة «{{status}}». يجب تحويل حالة الطلب إلى «جاهز» أولاً.",
  "shipping.orderNotReady.action": "تغيير حالة الطلب",
  "shipping.selectCarrier.title": "اختر شركة الشحن",
  "shipping.selectCarrier.field": "شركة الشحن",
  "shipping.selectCarrier.storefrontHint": "من الموقع (اختر مدينة/حي بوسطة المطابق)",
  "shipping.selectCarrier.none": "لا توجد شركة شحن متصلة بعد.",
  "shipping.selectCarrier.goToSettings": "الذهاب لإعدادات الشحن",
  "shipping.selectCarrier.continue": "متابعة",
  "shipping.selectCarrier.cancel": "إلغاء",
  "shipping.selectCarrier.goodsValue": "قيمة البضاعة (اختياري)",
  "shipping.selectCarrier.zone": "المنطقة",
  "shipping.selectCarrier.addressLine": "العنوان",
  "shipping.selectCarrier.addressLine.placeholder": "الشارع والعمارة والدور والشقة",
  "shipping.selectCarrier.landmark": "علامة مميزة",
  "shipping.selectCarrier.landmark.placeholder": "أقرب مكان معروف",
  "shipping.selectCarrier.recipient": "المستلم",
  "shipping.selectCarrier.recipientFirstName": "الاسم الأول",
  "shipping.selectCarrier.recipientLastName": "اسم العائلة",
  "shipping.selectCarrier.recipientPhone2": "هاتف ثانٍ (اختياري)",
  "shipping.selectCarrier.allowOpenPackage": "السماح للعميل بفتح الطرد قبل استلامه",
  "shipping.addressMissing.message": "أدخل عنوان التسليم بالأعلى قبل إنشاء الشحنة.",
  "shipping.addressMissing.editCustomer": "تعديل بيانات العميل",
  "customers.shippingInfo.title": "معلومات الشحن",

  "nav.finance": "المالية",
  "finance.title": "المالية",
  "finance.subtitle": "المصروفات وأوامر الشراء ومركز النقد والأرباح والخسائر والموردون.",
  "finance.hero.breadcrumb": "مسار التنقل",
  "finance.hero.home": "الرئيسية",
  "finance.hero.badgeTitle": "رؤية مالية أوضح",
  "finance.hero.badgeHint": "إدارة أسهل لعملك",
  "finance.forbidden": "لا تملك صلاحية الوصول إلى المالية.",
  "finance.saved": "تم الحفظ",
  "finance.saveFailed": "تعذّر الحفظ، حاول مرة أخرى.",
  "finance.loadMore": "تحميل المزيد",
  "finance.tab.suppliers": "الموردون",
  "finance.tab.purchaseOrders": "أوامر الشراء",
  "finance.tab.expenses": "المصروفات",
  "finance.tab.reports": "مركز النقد والأرباح والخسائر",
  "finance.actions.save": "حفظ",
  "finance.actions.cancel": "إلغاء",
  "finance.actions.create": "جديد",
  "finance.actions.edit": "تعديل",
  "finance.actions.archive": "أرشفة",
  "finance.actions.view": "عرض",
  "finance.actions.close": "إغلاق",
  "finance.status.active": "نشط",
  "finance.status.inactive": "مؤرشف",
  "finance.suppliers.empty": "لا يوجد موردون بعد.",
  "finance.suppliers.search.placeholder": "بحث بالاسم…",
  "finance.suppliers.filter.activeOnly": "النشطون فقط",
  "finance.suppliers.field.name": "الاسم",
  "finance.suppliers.field.phone": "الهاتف",
  "finance.suppliers.field.email": "البريد الإلكتروني",
  "finance.suppliers.field.address": "العنوان",
  "finance.suppliers.field.taxId": "الرقم الضريبي",
  "finance.po.empty": "لا توجد أوامر شراء بعد.",
  "finance.po.filter.status": "الحالة",
  "finance.po.filter.clear": "مسح الفلاتر",
  "finance.po.filter.supplier": "المورد",
  "finance.po.field.supplier": "المورد",
  "finance.po.field.expectedDate": "التاريخ المتوقع",
  "finance.po.field.notes": "ملاحظات",
  "finance.po.field.number": "رقم أمر الشراء",
  "finance.po.field.status": "الحالة",
  "finance.po.field.variant": "المتغيّر",
  "finance.po.field.quantity": "الكمية",
  "finance.po.field.unitCost": "تكلفة الوحدة",
  "finance.po.form.addLine": "إضافة سطر",
  "finance.po.status.draft": "مسودة",
  "finance.po.status.ordered": "تم الطلب",
  "finance.po.status.partially_received": "استلام جزئي",
  "finance.po.status.received": "تم الاستلام",
  "finance.po.status.cancelled": "ملغى",
  "finance.po.detail.lines": "الأسطر",
  "finance.po.actions.receive": "استلام",
  "finance.po.actions.pay": "تسجيل دفعة",
  "finance.po.receive.warehouse": "المستودع",
  "finance.po.receive.note": "يتم استلام الكمية المتبقية بالكامل من كل سطر إلى هذا المستودع.",
  "finance.po.receive.submit": "تأكيد الاستلام",
  "finance.po.pay.amount": "المبلغ",
  "finance.po.pay.method": "طريقة الدفع",
  "finance.po.pay.submit": "تسجيل الدفعة",
  "finance.expenses.empty": "لا توجد مصروفات بعد.",
  "finance.expenses.field.category": "الفئة",
  "finance.expenses.field.amount": "المبلغ",
  "finance.expenses.field.incurredAt": "التاريخ",
  "finance.expenses.field.notes": "ملاحظات",
  "finance.expenses.field.supplier": "المورد",
  "finance.expenses.filter.category": "الفئة",
  "finance.expenses.currency": "EGP",
  "finance.expenses.listTitle": "سجل المصروفات",
  "finance.expenses.add": "إضافة مصروف جديد",
  "finance.expenses.addTitle": "مصروف جديد",
  "finance.expenses.editTitle": "تعديل المصروف",
  "finance.expenses.editFor": "تعديل مصروف {{category}}",
  "finance.expenses.statsError": "تعذّر تحميل الإحصائيات.",
  "finance.expenses.stats.topCategory": "أعلى فئة مصروفات",
  "finance.expenses.stats.topCategoryShare": "من إجمالي المصروفات",
  "finance.expenses.stats.count": "عدد المصروفات",
  "finance.expenses.stats.total": "إجمالي المصروفات",
  "finance.expenses.stats.average": "متوسط المصروفات الشهري",
  "finance.expenses.stats.averageHint": "الإجمالي ÷ الشهور المنقضية",
  "finance.expenses.stats.vsPrevious": "مقارنة بنفس الفترة من العام السابق",
  "finance.expenses.stats.none": "لا توجد مصروفات",
  "finance.expenses.chart.monthly": "المصروفات الشهرية",
  "finance.expenses.chart.monthlyHint": "إجمالي المصروفات لكل شهر",
  "finance.expenses.chart.byCategory": "توزيع المصروفات حسب الفئة",
  "finance.expenses.chart.byCategoryHint": "نسبة كل فئة من إجمالي المصروفات",
  "finance.expenses.chart.year": "السنة",
  "finance.expenses.chart.refresh": "تحديث",
  "finance.expenses.chart.totalLabel": "إجمالي المصروفات",
  "finance.expenses.chart.empty": "لا توجد مصروفات في {{year}}.",
  "finance.expenses.chart.others": "أخرى",
  "finance.reports.dateFrom": "من",
  "finance.reports.dateTo": "إلى",
  "finance.reports.compareFrom": "المقارنة من",
  "finance.reports.compareTo": "المقارنة إلى",
  "finance.reports.actions.load": "تحميل",
  "finance.reports.cashCenter.title": "مركز النقد",
  "finance.reports.cashCenter.collected": "المُحصّل",
  "finance.reports.cashCenter.expenses": "المصروفات",
  "finance.reports.cashCenter.poPayments": "دفعات أوامر الشراء",
  "finance.reports.cashCenter.refunds": "المردودات",
  "finance.reports.cashCenter.shippingFees": "رسوم الشحن",
  "finance.reports.cashCenter.net": "صافي النقد",
  "finance.reports.pnl.title": "الأرباح والخسائر",
  "finance.reports.pnl.revenue": "الإيرادات",
  "finance.reports.pnl.cogs": "تكلفة البضاعة المباعة",
  "finance.reports.pnl.expenses": "المصروفات",
  "finance.reports.pnl.netIncome": "صافي الدخل",
  "finance.reports.pnl.previous": "الفترة السابقة",

  "nav.analytics": "التحليلات",
  "analytics.title": "التحليلات",
  "analytics.subtitle": "نظرات محسوبة على بياناتك — بلا توقعات، أرقام حقيقية فقط.",
  "analytics.forbidden": "لا تملك صلاحية الوصول إلى التحليلات.",
  "analytics.loadFailed": "تعذّر التحميل. حاول مرة أخرى.",
  "analytics.currency": "EGP",
  "analytics.vsPrevious": "مقارنة بالفترة السابقة",
  "analytics.tab.products": "المنتجات",
  "analytics.tab.profitability": "الربحية",
  "analytics.hero.home": "الرئيسية",
  "analytics.hero.breadcrumb": "مسار الصفحة",
  "analytics.hero.title.products": "التحليلات",
  "analytics.hero.title.profitability": "تحليل الربحية",
  "analytics.hero.subtitle.products": "اكتشف أفضل المنتجات أداءً وأقلها، وحلّل الربحية بدقة.",
  "analytics.hero.subtitle.profitability":
    "تقييم الأداء المالي وهامش الربح بناءً على المبيعات والمصروفات خلال الفترة المحددة.",
  "analytics.hero.badgeTitle.products": "بيانات تحوّل قرارات أفضل",
  "analytics.hero.badgeTitle.profitability": "معلومات أدق .. قرارات أفضل",
  "analytics.hero.badgeHint": "كل ما هنا محسوب من بياناتك أنت.",
  "analytics.window.from": "من",
  "analytics.window.to": "إلى",
  "analytics.window.granularity": "التجميع",
  "analytics.window.granularity.day": "يوم",
  "analytics.window.granularity.week": "أسبوع",
  "analytics.window.granularity.month": "شهر",
  "analytics.actions.export": "تصدير CSV",
  "analytics.export.done": "تم تنزيل التصدير",
  "analytics.export.failed": "تعذّر التصدير. حاول مرة أخرى.",
  "analytics.business.orderCount": "الطلبات",
  "analytics.business.collected": "المُحصّل",
  "analytics.business.averageOrderValue": "متوسط قيمة الطلب",
  "analytics.products.top": "الأعلى أداءً",
  "analytics.products.bottom": "الأقل أداءً",
  "analytics.products.topHint": "المنتجات ذات أعلى مبيعات خلال الفترة المحددة.",
  "analytics.products.bottomHint": "المنتجات ذات أقل مبيعات خلال الفترة المحددة.",
  "analytics.products.product": "المنتج",
  "analytics.products.unitsSold": "الوحدات المباعة",
  "analytics.products.unitsShort": "الوحدات",
  "analytics.products.revenue": "الإيرادات",
  "analytics.products.revenueWithUnit": "الإيرادات ({{unit}})",
  "analytics.products.count": "{{count}} منتجات",
  "analytics.products.viewAll": "عرض جميع المنتجات",
  "analytics.products.empty": "لا مبيعات في هذه الفترة.",
  "analytics.products.totalProducts": "إجمالي المنتجات",
  "analytics.products.newInPeriod": "+{{count}} جديدة في الفترة",
  "analytics.products.totalRevenue": "إجمالي الإيرادات",
  "analytics.products.averagePrice": "متوسط سعر المنتج",
  "analytics.profitability.sales": "إجمالي المبيعات",
  "analytics.profitability.cogs": "تكلفة البضاعة المباعة",
  "analytics.profitability.expenses": "إجمالي المصروفات",
  "analytics.profitability.netIncome": "صافي الربح",
  "analytics.profitability.margin": "هامش الربح",
  "analytics.profitability.chart.title": "المبيعات والمصروفات والربح",
  "analytics.profitability.chart.hint": "كل فترة داخل المدى المحدد، جنبًا إلى جنب.",
  "analytics.profitability.chart.empty": "لا حركة في هذه الفترة.",
  "analytics.profitability.split.title": "توزيع القيم",
  "analytics.profitability.split.total": "إجمالي الحركات",
  "analytics.profitability.indicators.title": "أبرز المؤشرات",
  "analytics.profitability.indicators.hint": "قراءات مشتقّة من أرقام الفترة.",
  "analytics.profitability.indicators.monthlyExpenses": "متوسط المصروفات الشهري",
  "analytics.profitability.indicators.monthlyProfit": "متوسط الربح الشهري",
  "analytics.profitability.indicators.perMonth": "شهريًا على مدى الفترة",
  "analytics.profitability.indicators.netMargin": "هامش الربح الصافي",
  "analytics.profitability.indicators.netMarginHint": "صافي الربح ÷ المبيعات",
  "analytics.profitability.indicators.breakEven": "نقطة التعادل",
  "analytics.profitability.indicators.breakEvenHint": "المبيعات اللازمة لتغطية المصروفات",
  "notifications.bell.label": "الإشعارات",
  "notifications.title": "الإشعارات",
  "notifications.empty.title": "لا توجد إشعارات بعد",
  "notifications.empty.description": "ستظهر هنا تحديثات طلباتك.",
  "notifications.loadFailed": "تعذّر تحميل الإشعارات.",
  "notifications.markAllRead": "تعليم الكل كمقروء",
  "notifications.preferences.link": "التفضيلات",
  "notifications.type.order.status_changed": "حالة الطلب",
  "notifications.type.payment.collected": "تحصيل الدفع",
  "notifications.type.order_vendor_group.assigned": "طلب تاجر جديد",
  "notifications.type.order.created": "طلب جديد",
  "notifications.content.orderCreated.title": "طلب جديد",
  "notifications.content.orderCreated.body":
    "وصل طلب جديد رقم {{orderNumber}} من {{customerName}}.",
  "notifications.content.orderCreated.bodyWithTotal":
    "وصل طلب جديد رقم {{orderNumber}} من {{customerName}} بقيمة {{total}}.",
  "notifications.content.orderStatusChanged.title": "تحديث حالة الطلب",
  "notifications.content.orderStatusChanged.body":
    "طلب {{customerName}} رقم {{orderNumber}} أصبح {{status}}.",
  "notifications.content.paymentCollected.title": "تم تحصيل دفعة",
  "notifications.content.paymentCollected.body":
    "تم تحصيل {{amount}} على طلب {{customerName}} رقم {{orderNumber}}.",
  "notifications.content.vendorGroupAssigned.title": "طلب جديد للتجهيز",
  "notifications.content.vendorGroupAssigned.body": "لديك طلب جديد لتجهيزه رقم {{orderNumber}}.",
  "notifications.type.message.received": "رسالة جديدة",
  "notifications.content.messageReceived.title": "رسالة جديدة",
  "notifications.content.messageReceived.body": "{{preview}}",
  "notifications.content.messageReceived.bodyNoPreview": "لديك رسالة جديدة.",
  "notifications.preferences.title": "تفضيلات الإشعارات",
  "notifications.preferences.subtitle": "اختر الطريقة التي تريد بها معرفة كل نوع من التحديثات.",
  "notifications.preferences.inApp": "داخل التطبيق",
  "notifications.preferences.webPush": "إشعارات الدفع",
  "notifications.push.title": "الإشعارات على هذا الجهاز",
  "notifications.push.enable": "تفعيل الإشعارات",
  "notifications.push.disable": "إيقافها على هذا الجهاز",
  "notifications.push.later": "ليس الآن",
  "notifications.push.enabled": "تم تفعيل الإشعارات على هذا الجهاز.",
  "notifications.push.disabled": "تم إيقاف الإشعارات على هذا الجهاز.",
  "notifications.push.denied":
    "المتصفح يحجب الإشعارات. اسمح بها من إعدادات الموقع ثم حاول مرة أخرى.",
  "notifications.push.failed": "تعذّر تفعيل الإشعارات. حاول مرة أخرى.",
  "notifications.push.onHint": "هذا الجهاز يستقبل الإشعارات حتى والتطبيق مغلق.",
  "notifications.push.offHint": "فعّلها لتصلك الإشعارات على هذا الجهاز حتى والتطبيق مغلق.",
  "notifications.push.blocked":
    "الإشعارات محجوبة لهذا الموقع. اسمح بها من إعدادات الموقع في متصفحك لتفعيلها.",
  "notifications.push.unsupported":
    "هذا المتصفح لا يدعم إشعارات الدفع. على الآيفون، أضف التطبيق إلى الشاشة الرئيسية أولاً.",
  "notifications.push.nudgeTitle": "لا يفوتك طلب",
  "notifications.push.nudgeBody": "تصلك الإشعارات على هذا الجهاز حتى والتطبيق مغلق.",
  "notifications.preferences.save": "حفظ",
  "notifications.preferences.saved": "تم حفظ التفضيلات.",
  "notifications.preferences.saveFailed": "تعذّر حفظ التفضيلات. حاول مرة أخرى.",

  "onboarding.start.title": "ابدأ الآن",
  "onboarding.start.subtitle": "أنشئ شركة جديدة أو انضم إلى شركة موجودة",
  "onboarding.start.create.title": "إنشاء شركة",
  "onboarding.start.create.description": "إنشاء مساحة عمل جديدة للشركة",
  "onboarding.start.join.title": "الانضمام لشركة",
  "onboarding.start.join.description": "أدخل رمز الدعوة للانضمام إلى شركة",

  "onboarding.create.title": "إنشاء شركة",
  "onboarding.create.field.name": "اسم الشركة",
  "onboarding.create.field.country": "الدولة",
  "onboarding.create.field.countryPlaceholder": "اختر الدولة",
  "onboarding.create.field.phone": "رقم الجوال",
  "onboarding.create.field.monthlyOrdersRange": "عدد الطلبات الشهرية",
  "onboarding.create.field.monthlyOrdersRangePlaceholder": "اختر عدد الطلبات",
  "onboarding.create.field.hasFacebook": "هل لديك صفحة فيسبوك؟",
  "onboarding.create.field.facebookHandle": "رابط أو اسم صفحة الفيسبوك",
  "onboarding.create.field.hasInstagram": "هل لديك حساب انستقرام؟",
  "onboarding.create.field.instagramHandle": "رابط أو اسم حساب الانستقرام",
  "onboarding.create.field.hasWebsite": "هل لديك موقع إلكتروني؟",
  "onboarding.create.field.websiteUrl": "رابط الموقع الإلكتروني",
  "onboarding.create.field.shippingCarrier": "شركة الشحن",
  "onboarding.create.field.shippingCarrierPlaceholder": "مثال: DHL, FedEx, أرامكس",
  "onboarding.create.submit": "إنشاء شركة",
  "onboarding.monthlyOrders.under_100": "أقل من 100",
  "onboarding.monthlyOrders.100_500": "100 - 500",
  "onboarding.monthlyOrders.500_1000": "500 - 1,000",
  "onboarding.monthlyOrders.1000_2000": "1,000 - 2,000",
  "onboarding.monthlyOrders.2000_5000": "2,000 - 5,000",
  "onboarding.monthlyOrders.over_5000": "أكثر من 5,000",

  "onboarding.join.title": "الانضمام لشركة",
  "onboarding.join.kind.label": "الانضمام عبر",
  "onboarding.join.kind.invitation": "دعوة شركة",
  "onboarding.join.kind.warehouseCode": "كود تاجر / مستودع",
  "onboarding.join.field.code": "رمز الدعوة",
  "onboarding.join.field.warehouseCode": "كود دعوة المستودع",
  "onboarding.join.submit": "الانضمام",
  "onboarding.error.generic": "حدث خطأ ما. حاول مرة أخرى.",
  "onboarding.error.invalidInvitation": "رمز الدعوة غير صالح أو منتهي الصلاحية.",
  "onboarding.error.invalidWarehouseCode": "كود دعوة المستودع غير صالح أو تم إلغاؤه.",
  "onboarding.country.eg": "مصر",
  "onboarding.country.sa": "السعودية",
  "onboarding.country.ae": "الإمارات العربية المتحدة",
  "onboarding.country.kw": "الكويت",
  "onboarding.country.qa": "قطر",
  "onboarding.country.bh": "البحرين",
  "onboarding.country.om": "عُمان",
  "onboarding.country.jo": "الأردن",
  "onboarding.country.iq": "العراق",
  "onboarding.country.other": "أخرى",

  "vendor.dashboard.title": "حساب التاجر",
  "vendor.dashboard.myOrders": "طلباتي",
  "vendor.dashboard.subtitle": "المنتجات الموجّهة لمستودعك فقط.",
  "vendor.dashboard.empty": "لا توجد طلبات بعد.",
  "vendor.dashboard.order": "الطلب",
  "vendor.dashboard.saved": "تم الحفظ.",
  "vendor.dashboard.saveFailed": "تعذّر الحفظ. حاول مرة أخرى.",
  "vendor.dashboard.updatedAt": "آخر تحديث",
  "vendor.dashboard.section.active": "نشط",
  "vendor.dashboard.section.delivered": "تم التسليم",
  "vendor.dashboard.advanceTo.processing": "بدء المعالجة",
  "vendor.dashboard.advanceTo.ready": "تحديد كجاهز",
  "vendor.dashboard.advanceTo.delivered": "تحديد كمُسلَّم",
  "vendor.group.status.new": "جديد",
  "vendor.group.status.processing": "جاري المعالجة",
  "vendor.group.status.ready": "جاهز",
  "vendor.group.status.delivered": "تم التسليم",

  "orders.vendorGroups.setStatus": "تغيير الحالة",
  "orders.vendorGroups.overrideHint": "يستطيع المدير تصحيح حالة التاجر في الاتجاهين.",
  "orders.vendorGroups.confirmBack.title": "إرجاع مجموعة التاجر خطوة للخلف؟",
  "orders.vendorGroups.confirmBack.description":
    "التاجر حدّدها بالفعل كـ«{{from}}». إرجاعها إلى «{{to}}» يُسجَّل باسمك.",
  "orders.vendorGroups.confirmBack.yes": "نعم، أرجِعها",
  "orders.vendorGroups.confirmBack.no": "اتركها كما هي",
  "orders.vendorGroups.moved": "تم نقل مجموعة التاجر إلى {{status}}.",
  "orders.vendorGroups.moveFailed": "تعذّر تغيير الحالة. حاول مرة أخرى.",

  "vendor.nav.label": "قائمة تنقّل التاجر",
  "vendor.nav.home": "الرئيسية",
  "vendor.nav.orders": "الطلبات",
  "vendor.nav.products": "المنتجات",

  "vendor.dashboard.overview.title": "نظرة عامة",
  "vendor.dashboard.kpi.total": "إجمالي الطلبات",
  "vendor.dashboard.kpi.new": "جديدة",
  "vendor.dashboard.kpi.processing": "جاري المعالجة",
  "vendor.dashboard.kpi.ready": "جاهزة",
  "vendor.dashboard.kpi.delivered": "تم التسليم",
  "vendor.dashboard.recent.title": "أحدث الطلبات",
  "vendor.dashboard.recent.viewAll": "عرض كل الطلبات",

  "vendor.orders.search.placeholder": "ابحث برقم الطلب",
  "vendor.orders.tabs.all": "الكل",
  "vendor.orders.sort.newestFirst": "الأحدث أولًا",
  "vendor.orders.sort.oldestFirst": "الأقدم أولًا",

  "vendor.orderDetail.back": "العودة إلى الطلبات",
  "vendor.orderDetail.notFound.title": "الطلب غير موجود",
  "vendor.orderDetail.notFound.description": "هذا الطلب غير مُسند إلى مستودعك، أو غير موجود.",
  "vendor.orderDetail.field.warehouse": "المستودع",
  "vendor.orderDetail.items.title": "منتجاتي",
  "vendor.orderDetail.status.title": "الحالة",

  "vendor.status.more": "النقل إلى حالة لاحقة",
  "vendor.orders.dragHint": "تلميح: اسحب الطلب وأفلته على أي حالة لنقله إليها.",
  "vendor.orders.dropHere": "أفلت هنا للنقل",
  "vendor.orders.board.empty": "لا توجد طلبات هنا",
  "vendor.orders.moved": "تم نقل الطلب رقم {{order}} إلى {{status}}.",
  "vendor.orders.moveFailed": "تعذّر نقل الطلب، وأُعيد إلى حالته السابقة.",
  "vendor.orders.panelClose": "إغلاق الطلب",

  "vendor.products.title": "منتجاتي",
  "vendor.products.subtitle": "المنتجات الموجودة في مستودعك فقط.",
  "vendor.products.empty": "لا توجد منتجات في مستودعك بعد.",
  "vendor.products.available": "الكمية المتاحة",

  "nav.messages": "الرسائل",
  "nav.desc.messages": "تواصل مع التجّار",
  "vendor.nav.messages": "الرسائل",
  "vendor.messages.title": "الرسائل",
  "vendor.messages.subtitle": "محادثتك مع فريق الشركة.",

  "messaging.title": "الرسائل",
  "messaging.subtitle": "المحادثات مع تجّارك.",
  "messaging.forbidden": "لا تملك صلاحية الوصول إلى الرسائل.",
  "messaging.actions.new": "محادثة جديدة",
  "messaging.actions.loadMore": "تحميل المزيد",
  "messaging.empty.title": "لا توجد محادثات بعد",
  "messaging.empty.description": "ابدأ محادثة مع أحد التجّار لتظهر هنا.",
  "messaging.thread.close": "إغلاق المحادثة",
  "messaging.thread.empty": "لا توجد رسائل بعد. ابدأ الحديث!",
  "messaging.thread.loadOlder": "تحميل رسائل أقدم",
  "messaging.thread.archived": "هذه المحادثة مؤرشَفة.",
  "messaging.thread.noMessagesYet": "لا توجد رسائل بعد",
  "messaging.message.deleted": "تم حذف هذه الرسالة.",
  "messaging.lightbox.close": "إغلاق",
  "messaging.lightbox.previous": "الصورة السابقة",
  "messaging.lightbox.next": "الصورة التالية",
  "messaging.composer.placeholder": "اكتب رسالة… استخدم @ للإشارة إلى طلب",
  "messaging.composer.attach": "إرفاق صورة",
  "messaging.composer.send": "إرسال",
  "messaging.composer.sendFailed": "تعذّر إرسال الرسالة. حاول مرة أخرى.",
  "messaging.composer.attachFailed": "تعذّر رفع الصورة. حاول مرة أخرى.",
  "messaging.composer.attachmentsUnavailable": "رفع الصور غير متاح مؤقتًا.",
  "messaging.composer.removeMention": "إزالة الإشارة",
  "messaging.composer.removeAttachment": "إزالة الصورة",
  "messaging.mention.empty": "لا توجد طلبات مطابقة",
  "messaging.picker.title": "بدء محادثة",
  "messaging.picker.placeholder": "اختر تاجرًا",
  "messaging.picker.cancel": "إلغاء",
  "messaging.picker.start": "بدء",
  "messaging.picker.loadFailed": "تعذّر تحميل التجّار. حاول مرة أخرى.",
  "messaging.picker.openFailed": "تعذّر فتح المحادثة. حاول مرة أخرى.",
};

export const dictionaries = { ar, en } as const;

export type Locale = keyof typeof dictionaries;

/** Text direction for a locale. Arabic is RTL; English is LTR. */
export function directionForLocale(locale: Locale): "rtl" | "ltr" {
  return locale === "ar" ? "rtl" : "ltr";
}
